import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, Save, Play, Upload, Download, Wand2, Loader2, ImagePlus, X, Eye, RefreshCcw, Copy, Check, ExternalLink, Sparkles, Maximize2 } from "lucide-react";
import {
  getStoryboard,
  updateStoryboardMeta,
  addStoryboardScene,
  updateStoryboardScene,
  deleteStoryboardScene,
  startStoryboardScenePreview,
  assembleStoryboard,
  resetStoryboardAssemble,
  approveSceneVersion,
  cancelScenePreview,
  deleteSceneVersion,
  generateStoryboardDescription,
  regenerateStoryboardThumbnail,
  uploadStoryboardThumbnail,
  generateSceneImage,
  regenerateAllSceneImages,
  uploadSceneReferenceImage,
  deleteSceneReferenceImage,
  getScenePrompts,
  publishStoryboardToYouTube,
  listAutomationQueue,
  cancelAutomationJob,
  spellcheckSceneImage,
  spellcheckThumbnail,
  listVoices,
} from "@/lib/api";
import VideoProviderSelect from "@/components/VideoProviderSelect";
import ThumbnailRegenerateDialog from "@/components/ThumbnailRegenerateDialog";
import VoiceSamplePlayButton from "@/components/VoiceSamplePlayButton";
import SceneGalleryGrid from "@/components/SceneGalleryGrid";
import StoryboardBumpersRow from "@/components/StoryboardBumpersRow";
import AutomationProgressBanner from "@/components/AutomationProgressBanner";
import SpellcheckBadge from "@/components/SpellcheckBadge";
import CharacterConsistencyPanel from "@/components/CharacterConsistencyPanel";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Friendly display labels for each video provider id stored on a
 * version. Keep in sync with `VIDEO_PROVIDERS` in
 * `/app/backend/video_providers.py`.
 */
const PROVIDER_LABEL = {
  "seedance-2.0": "Seedance 2.0",
  "kling-2.5": "Kling 2.5",
  "veo3-direct": "Veo 3",
  "runway": "Runway",
  "luma-ray-2": "Luma Ray-2",
  "minimax-hailuo-02": "MiniMax Hailuo-02",
  "wan-2.6": "Wan 2.6",
};

/**
 * Storyboard editor for a single video. Renders every scene as an
 * editable card (image + description + per-scene Seedance preview),
 * plus an "Add scene" form at the bottom. Auto-refreshes every 5s
 * while any scene's preview is `running` so the user sees the MP4
 * appear without manually reloading.
 */
export default function StoryboardDetail() {
  const { storyboardId } = useParams();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  // "gallery" = compact grid (default landing view — one card per
  // scene with an approve toggle). "detail" = the classic linear
  // editor with every scene fully expanded.
  //
  // Design: gallery is ALWAYS the landing view for a fresh
  // navigation into a storyboard. If the user switches to detail,
  // that's an intra-session preference on THIS storyboard only —
  // going back to the storyboard list and opening a different (or
  // the same) storyboard again resets to gallery. This is what the
  // user asked for after seeing older browsers stuck on the old
  // linear view due to a cached localStorage flag.
  const [viewMode, setViewMode] = useState("gallery");
  // Reset whenever we navigate to a different storyboard so the
  // gallery default is honoured cross-storyboard.
  useEffect(() => {
    setViewMode("gallery");
    // One-time cleanup of the legacy sticky key from previous
    // builds. Its presence was the reason some users kept landing
    // in the old detail view even after this page defaults to
    // gallery. Safe to run every mount — noop once the key is gone.
    try { localStorage.removeItem("ytm.storyboard.view_mode"); } catch { /* ignore */ }
  }, [storyboardId]);
  // When a gallery card is clicked we switch to detail view AND
  // scroll to the matching anchor. Held in a ref so the scroll can
  // fire once the detail cards are mounted.
  const focusSceneIdRef = useRef(null);
  const pollRef = useRef(null);
  // Request-generation counter. Every load() invocation gets a new
  // id. When the response arrives we drop it if a NEWER load() has
  // already been kicked off — prevents stale poll responses from
  // overwriting the post-delete (or post-approve, etc) state.
  const loadGenRef = useRef(0);

  const load = useCallback(async () => {
    const my = ++loadGenRef.current;
    try {
      const doc = await getStoryboard(storyboardId);
      if (my !== loadGenRef.current) return; // a newer load() won
      setBoard(doc);
    } catch (e) {
      if (my !== loadGenRef.current) return;
      toast.error(e?.response?.data?.detail || "Couldn't load storyboard");
    } finally {
      if (my === loadGenRef.current) setLoading(false);
    }
  }, [storyboardId]);

  useEffect(() => { load(); }, [load]);

  // Poll while any scene is rendering so previews appear automatically.
  useEffect(() => {
    if (!board) return undefined;
    const anyRunning = (board.scenes || []).some((s) => s.preview_status === "running");
    const bulkRunning = (board.bulk_regen || {}).status === "running";
    if (!anyRunning && !bulkRunning) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (pollRef.current) return undefined;
    // Poll faster while a bulk regen is streaming (each scene takes
    // ~15s and progress lives on the doc) so the counter looks live.
    pollRef.current = setInterval(() => { load(); }, bulkRunning ? 3000 : 5000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [board, load]);

  // -------------------- Automation job banner --------------------
  // If the user just spawned this storyboard from Idea Engine (or hit
  // "Automate" on Saved Story Ideas) there's a background job walking
  // the scene list rendering images + videos. We surface that same
  // banner the Idea Engine page shows, right at the top of THIS page
  // so the user sees progress + can cancel without having to bounce
  // back to the Storyboards list.
  const [autoJob, setAutoJob] = useState(null);
  const [autoJobDismissed, setAutoJobDismissed] = useState(false);
  const autoJobPollRef = useRef(null);

  const loadAutoJob = useCallback(async () => {
    if (!storyboardId) return;
    try {
      const res = await listAutomationQueue();
      const items = Array.isArray(res?.items) ? res.items : [];
      // Newest first — pick the freshest job pointing at this board.
      const match = items.find((j) => j?.storyboard_id === storyboardId);
      setAutoJob(match || null);
    } catch { /* silent — banner just doesn't render */ }
  }, [storyboardId]);

  useEffect(() => {
    // Reset dismissal + refetch whenever the URL changes.
    setAutoJobDismissed(false);
    loadAutoJob();
  }, [storyboardId, loadAutoJob]);

  // Auto-poll every 4s while the job is queued/running so the banner
  // ticks. Stop as soon as it hits a terminal state (or disappears).
  useEffect(() => {
    const status = autoJob?.status;
    const isLive = status === "queued" || status === "running";
    if (!isLive) {
      if (autoJobPollRef.current) {
        clearInterval(autoJobPollRef.current);
        autoJobPollRef.current = null;
      }
      // When a live job just flipped to `completed`, refresh the
      // storyboard so newly-attached scenes show up without needing
      // a manual reload.
      if (status === "completed") { load(); }
      return undefined;
    }
    if (autoJobPollRef.current) return undefined;
    autoJobPollRef.current = setInterval(() => {
      loadAutoJob();
      // Piggy-back a storyboard refresh so scenes populate in-place
      // as the worker attaches them (they arrive via the same doc).
      load();
    }, 4000);
    return () => {
      if (autoJobPollRef.current) {
        clearInterval(autoJobPollRef.current);
        autoJobPollRef.current = null;
      }
    };
  }, [autoJob?.status, loadAutoJob, load]);

  if (loading) {
    return (
      <main className="px-6 py-8 max-w-5xl mx-auto">
        <div className="font-mono text-xs text-zinc-500">Loading…</div>
      </main>
    );
  }
  if (!board) {
    return (
      <main className="px-6 py-8 max-w-5xl mx-auto">
        <div className="text-zinc-400">Storyboard not found.</div>
      </main>
    );
  }

  return (
    <main
      data-testid="storyboard-detail-page"
      data-storyboard-id={board.storyboard_id}
      className="px-6 py-8 max-w-5xl mx-auto space-y-8"
    >
      {board.role === "admin" && (
        <div
          data-testid="admin-readonly-banner"
          className="border border-yellow-400/60 bg-yellow-400/[0.08] rounded-sm px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
        >
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
              // ADMIN VIEW · READ-ONLY
            </div>
            <div className="text-sm text-zinc-100 leading-snug mt-1">
              You&apos;re viewing{" "}
              <b className="text-yellow-300">{board.owner_email || board.owner_user_id || "another user"}</b>
              ’s storyboard as an admin. Edits, renders, deletes are
              disabled — switch to your own storyboards to make changes.
            </div>
          </div>
          <Link
            to="/admin"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 border border-yellow-400/40 hover:bg-yellow-400 hover:text-black px-3 py-1.5 rounded-sm transition-colors shrink-0"
          >
            ← back to /admin
          </Link>
        </div>
      )}
      {autoJob && !autoJobDismissed && (
        <div data-testid="storyboard-automation-banner">
          <AutomationProgressBanner
            job={autoJob}
            onDismiss={() => setAutoJobDismissed(true)}
            onCancel={async () => {
              if (!window.confirm(
                `Cancel "${autoJob.video_name || "this job"}"? Any scenes already rendered will stay on the storyboard.`,
              )) return;
              try {
                await cancelAutomationJob(autoJob.job_id);
                toast.success("Job cancelled — worker will bail before the next scene");
                loadAutoJob();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't cancel job");
              }
            }}
          />
        </div>
      )}
      <header className="space-y-2">
        <Link
          to="/storyboards"
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-300"
        >
          <ChevronLeft className="w-3 h-3" /> back to storyboards
        </Link>
        <VideoNameEditor board={board} onSaved={load} />
        <ThumbnailHeroPanel board={board} onChanged={load} />
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          {(board.scenes || []).length} scene
          {(board.scenes || []).length === 1 ? "" : "s"} ·
          {" "}preview clips render as 15s Seedance 2.0 videos (~$0.90 each)
        </p>
        <VideoDescriptionPanel board={board} onChanged={load} />
      </header>

      <CharacterConsistencyPanel
        storyboardId={board.storyboard_id}
        scenes={board.scenes || []}
      />

      <section
        data-testid="storyboard-scene-grid"
        className="space-y-5"
      >
        <div className="flex items-center gap-3 border-b border-zinc-900 pb-3 flex-wrap">
          <span className="inline-flex items-center justify-center min-w-[2.75rem] h-7 px-2 font-mono text-[10px] font-black uppercase tracking-[0.25em] bg-yellow-400 text-black rounded-sm no-pill">
            SCENES
          </span>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
            // PRODUCTION LINE
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            · {(board.scenes || []).length} station{(board.scenes || []).length === 1 ? "" : "s"}
          </div>
          {board.role !== "admin" && (
            <RegenerateAllScenesButton
              board={board}
              onKicked={load}
            />
          )}
          <div className="ml-auto inline-flex border border-zinc-800 rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("gallery")}
              data-testid="storyboard-view-mode-gallery"
              aria-pressed={viewMode === "gallery"}
              className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors ${
                viewMode === "gallery"
                  ? "bg-yellow-400 text-black"
                  : "text-zinc-400 hover:text-yellow-300"
              }`}
            >
              Gallery
            </button>
            <button
              type="button"
              onClick={() => setViewMode("detail")}
              data-testid="storyboard-view-mode-detail"
              aria-pressed={viewMode === "detail"}
              className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border-l border-zinc-800 transition-colors ${
                viewMode === "detail"
                  ? "bg-yellow-400 text-black"
                  : "text-zinc-400 hover:text-yellow-300"
              }`}
            >
              Detail
            </button>
          </div>
        </div>
        <BulkRegenStatusBanner board={board} />

        {viewMode === "gallery" ? (
          <SceneGalleryGrid
            scenes={board.scenes || []}
            storyboardId={board.storyboard_id}
            readOnly={board.role === "admin"}
            onOpenScene={(sceneId) => {
              focusSceneIdRef.current = sceneId;
              setViewMode("detail");
              // Fires after the detail cards mount; the id-based
              // anchor lives on each SceneCard root element.
              setTimeout(() => {
                const el = document.querySelector(
                  `[data-scene-anchor="${sceneId}"]`,
                );
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                focusSceneIdRef.current = null;
              }, 60);
            }}
            onChanged={load}
          />
        ) : (
          <>
            {(board.scenes || []).map((scene, idx) => (
              <div key={scene.scene_id} className="relative">
                {/* Amber station number tag pinned to the top-left of
                    each scene card so the storyboard reads like a
                    numbered production line — matches the Content
                    Factory conveyor-belt language. */}
                <div className="absolute -left-3 top-3 z-10 hidden lg:flex items-center gap-2 pointer-events-none">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-400 text-black font-mono text-[11px] font-black no-pill">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-yellow-400/60">
                    station
                  </span>
                </div>
                <SceneCard
                  scene={scene}
                  storyboardId={board.storyboard_id}
                  stillsOnly={(board.post_format || "video") === "stills"}
                  onChanged={load}
                />
              </div>
            ))}
            {(board.scenes || []).length === 0 && (
              <div className="border border-dashed border-zinc-800 rounded-sm p-6 text-center text-sm text-zinc-500">
                No scenes yet. Add the first one below.
              </div>
            )}
          </>
        )}
      </section>

      {/* Add scene lives directly under the last scene card so the
          user can extend the storyboard without scrolling past the
          Final Video panel. Collapsed by default — the form is
          long and the panel below (Assemble & Approve) is the more
          common next click. */}
      <AddSceneCollapsible
        storyboardId={board.storyboard_id}
        existingScenes={board.scenes || []}
        onAdded={load}
      />

      <AssembleFinalVideoPanel board={board} onChanged={load} />
    </main>
  );
}

function RegenerateAllScenesButton({ board, onKicked }) {
  const [busy, setBusy] = useState(false);
  const total = (board.scenes || []).length;
  const bulk = board.bulk_regen || {};
  const running = bulk.status === "running";
  const disabled = busy || running || total === 0;

  const onClick = async () => {
    if (disabled) return;
    const ok = window.confirm(
      `Regenerate ALL ${total} scene image${total === 1 ? "" : "s"}? `
      + `This replaces every current scene image with a fresh AI render. `
      + `Cannot be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      await regenerateAllSceneImages(board.storyboard_id);
      toast.success(`Kicked off ${total} scene render${total === 1 ? "" : "s"}. Check back in a few minutes.`);
      onKicked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start bulk regeneration");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="storyboard-regenerate-all-scenes-btn"
      title={
        running
          ? "A bulk regeneration is already running — wait for it to finish"
          : "Re-render every scene image via ChatGPT (gpt-image-1). No placards guaranteed by the post-render vision gate."
      }
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border rounded-sm transition-colors ${
        disabled
          ? "border-zinc-800 text-zinc-600 cursor-not-allowed"
          : "border-yellow-400/60 text-yellow-300 hover:bg-yellow-400 hover:text-black"
      }`}
    >
      {busy || running
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : <RefreshCcw className="w-3 h-3" />}
      {running
        ? `regen ${bulk.done ?? 0}/${bulk.total ?? total}`
        : "regenerate all scenes"}
    </button>
  );
}


function BulkRegenStatusBanner({ board }) {
  const bulk = board.bulk_regen || {};
  const status = bulk.status;
  if (!status || status === "idle") return null;
  const done = bulk.done ?? 0;
  const total = bulk.total ?? (board.scenes || []).length;
  const failed = bulk.failed ?? 0;
  const running = status === "running";
  const partial = status === "partial";
  const completed = status === "completed";
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div
      data-testid="storyboard-bulk-regen-banner"
      className={`border rounded-sm px-4 py-3 space-y-2 ${
        running
          ? "border-yellow-400/60 bg-yellow-400/[0.06]"
          : partial
            ? "border-orange-400/60 bg-orange-400/[0.06]"
            : "border-emerald-400/50 bg-emerald-400/[0.05]"
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`font-mono text-[10px] font-black uppercase tracking-[0.3em] ${
          running ? "text-yellow-400" : partial ? "text-orange-400" : "text-emerald-400"
        }`}>
          {running ? "// BULK REGEN RUNNING" : partial ? "// BULK REGEN COMPLETED (with errors)" : "// BULK REGEN COMPLETED"}
        </span>
        <span className="font-mono text-[11px] text-zinc-300" data-testid="storyboard-bulk-regen-progress">
          {done}/{total} rendered
          {failed > 0 ? ` · ${failed} failed` : ""}
        </span>
        {running && bulk.current_scene_id && (
          <span className="font-mono text-[10px] text-zinc-500">
            rendering {bulk.current_scene_id}…
          </span>
        )}
      </div>
      <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
        <div
          className={`h-full transition-all ${
            running ? "bg-yellow-400" : partial ? "bg-orange-400" : "bg-emerald-400"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {completed && (
        <div className="text-[11px] text-zinc-400">
          All scenes re-rendered. Each pass was auto-checked for
          number placards and re-rolled up to 3 times if a digit
          slipped in.
        </div>
      )}
      {partial && bulk.last_error && (
        <div className="text-[11px] text-orange-300">
          Last error: <span className="font-mono">{bulk.last_error}</span>
        </div>
      )}
    </div>
  );
}


function AddSceneCollapsible({ storyboardId, existingScenes, onAdded }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <div className="my-6 flex justify-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="storyboard-add-scene-open-btn"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] border border-cyan-400/60 hover:border-cyan-300 text-cyan-200 hover:text-cyan-100 bg-cyan-400/5 hover:bg-cyan-400/10 px-4 py-2.5 rounded-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Add scene
        </button>
      </div>
    );
  }
  return (
    <div className="my-6">
      <AddSceneForm
        storyboardId={storyboardId}
        existingScenes={existingScenes}
        onAdded={(...args) => {
          setOpen(false);
          onAdded?.(...args);
        }}
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="storyboard-add-scene-close-btn"
          className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
        >
          ✕ close add-scene form
        </button>
      </div>
    </div>
  );
}

function StitchHeartbeat({ heartbeatIso, startedIso, stage }) {
  // Recompute the "seconds since last heartbeat" on a 1-second tick.
  // We hold the tick in state so React re-renders and the label
  // freshens even when the outer poll (5s) hasn't fired yet.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!heartbeatIso) return null;
  const ageSec = Math.max(0, Math.floor((now - new Date(heartbeatIso).getTime()) / 1000));
  // Backend writes every ~5s; 20s+ = probably wedged; 45s+ = dead.
  const level = ageSec > 45 ? "dead" : ageSec > 20 ? "stale" : "live";
  const color = {
    live: "text-emerald-400 border-emerald-500/40",
    stale: "text-yellow-400 border-yellow-500/40",
    dead: "text-red-300 border-red-500/50",
  }[level];
  const dotColor = {
    live: "bg-emerald-400 animate-pulse",
    stale: "bg-yellow-400",
    dead: "bg-red-400",
  }[level];
  const fmtAge = ageSec < 60
    ? `${ageSec}s ago`
    : `${Math.floor(ageSec / 60)}m${(ageSec % 60).toString().padStart(2, "0")}s ago`;
  const title = level === "dead"
    ? "No heartbeat from the stitch task in over 45s — the job is almost certainly dead. Click Force reset to retry."
    : level === "stale"
    ? "The stitch task hasn't checked in for more than 20s — it may be stuck. Give it a moment, then Force reset if nothing changes."
    : "Stitch task is alive and encoding.";

  // Parse a "pair N/M" progress marker out of the stage string if
  // present. Backend writes `encoding · pair 3/10` after each
  // pairwise xfade iteration, so we can render a real progress bar
  // instead of the user having to guess where we are.
  const pairMatch = stage && stage.match(/pair\s+(\d+)\s*\/\s*(\d+)/i);
  const pairDone = pairMatch ? Number(pairMatch[1]) : null;
  const pairTotal = pairMatch ? Number(pairMatch[2]) : null;
  const pct = pairMatch ? Math.min(100, Math.round((pairDone / pairTotal) * 100)) : null;

  // Compute an ETA from actual wall time elapsed since the stitch
  // started. Each pairwise iteration is a 2-input encode of similar
  // shape, so avg-per-iteration = elapsed / done, and the projection
  // is avg × (total - done). We only surface it once at least one
  // pair has completed — before then we have nothing to project.
  let etaLabel = null;
  if (pairMatch && startedIso && pairDone > 0 && pairDone < pairTotal) {
    const startedMs = new Date(startedIso).getTime();
    if (!Number.isNaN(startedMs)) {
      const elapsedSec = Math.max(1, (now - startedMs) / 1000);
      const avgSec = elapsedSec / pairDone;
      const remainingSec = Math.round(avgSec * (pairTotal - pairDone));
      if (remainingSec < 60) {
        etaLabel = `~${remainingSec}s remaining`;
      } else {
        const mm = Math.floor(remainingSec / 60);
        const ss = remainingSec % 60;
        etaLabel = ss > 0
          ? `~${mm}m ${ss}s remaining`
          : `~${mm} min remaining`;
      }
    }
  }

  return (
    <div className="flex flex-col gap-2 w-fit">
      <div
        data-testid="storyboard-stitch-heartbeat"
        className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] border px-2 py-1 rounded-sm w-fit ${color}`}
        title={title}
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
        <span>
          {level === "live" ? "encoding" : level === "stale" ? "may be stuck" : "no heartbeat"}
          {stage ? ` · ${stage}` : ""}
          {" · "}
          {fmtAge}
        </span>
      </div>
      {pairMatch && (
        <div
          data-testid="storyboard-stitch-progress"
          className="flex flex-col gap-1 w-72"
        >
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            <span>
              pair {pairDone} <span className="text-zinc-700">of</span> {pairTotal}
            </span>
            <span className="text-zinc-400">
              {pct}%
              {etaLabel ? <span className="text-zinc-600"> · {etaLabel}</span> : null}
            </span>
          </div>
          <div className="h-1.5 bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <div
              className="h-full bg-yellow-400 transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AssembleFinalVideoPanel({ board, onChanged }) {
  // Stitch only the APPROVED scene versions into ONE MP4 with hblur
  // xfade transitions between scenes. Lit up only once the user has
  // approved AT LEAST 2 scene takes — production cut is gated until
  // they've signed off on the cuts they want shipped. Polls every 5s
  // while running.
  //
  // Stills-mode channels take a different assembly path — no
  // per-scene video takes exist, so the stitch runs over the per-scene
  // STILLS with Ken-Burns pan/zoom. The unlock gate is different too:
  // as long as every scene has a generated image, we can assemble.
  const stillsOnly = (board?.post_format || "video") === "stills";
  const scenes = board?.scenes || [];
  const approvedScenes = scenes.filter(
    (s) => (s.approved_version_id || "").trim().length > 0,
  );
  const approvedCount = approvedScenes.length;
  const scenesWithImages = scenes.filter(
    (s) => (s.image_url || "").trim().length > 0 || (s.image_gridfs_id || "").trim().length > 0,
  );
  const stillsReadyCount = scenesWithImages.length;
  const MIN_APPROVED = 2;
  const enoughApproved = stillsOnly
    ? (stillsReadyCount >= 2 && stillsReadyCount === scenes.length)
    : (approvedCount >= MIN_APPROVED);
  const status = board?.assembled_status || "idle";
  const url = board?.assembled_url || "";
  const error = board?.assembled_error || "";
  const sceneCount = board?.assembled_scene_count || 0;
  const durSec = board?.assembled_duration_sec || 0;
  const [submitting, setSubmitting] = useState(false);
  // Live MM:SS elapsed counter while ffmpeg is stitching. Driven by
  // server-recorded `assembled_started_at` so reloading the page
  // mid-stitch still shows the correct elapsed time.
  const [assembleElapsedSec, setAssembleElapsedSec] = useState(0);
  useEffect(() => {
    if (status !== "running") { setAssembleElapsedSec(0); return undefined; }
    const startMs = board?.assembled_started_at
      ? new Date(board.assembled_started_at).getTime()
      : Date.now();
    const tick = () => {
      setAssembleElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status, board?.assembled_started_at]);

  useEffect(() => {
    if (status !== "running") return undefined;
    const id = setInterval(onChanged, 5000);
    return () => clearInterval(id);
  }, [status, onChanged]);

  const fire = async () => {
    setSubmitting(true);
    try {
      await assembleStoryboard(board.storyboard_id);
      toast.success(okStart);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "not_enough_approved") {
        toast.error(
          `Need at least ${detail.min_required ?? MIN_APPROVED} approved scene takes — you have ${detail.approved_count ?? 0}.`,
        );
      } else if (typeof detail === "object" && detail?.error === "stills_images_missing") {
        toast.error(
          detail.message || "Some scenes still need images before the slideshow can assemble.",
        );
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't start assembly");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const okStart = stillsOnly ? "Assembling your still-slideshow with narration…" : "Stitching your approved scenes together…";

  const downloadFinal = async () => {
    if (!url) return;
    try {
      const fileName = `${(board.video_name || "storyboard").replace(/[^a-z0-9_-]+/gi, "-")}.mp4`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`download failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (e) {
      toast.error(e?.message || "Couldn't download");
    }
  };

  const fmtDur = (s) => {
    if (!s || s <= 0) return "";
    const m = Math.floor(s / 60);
    const ss = Math.round(s - m * 60);
    return `${m}:${String(ss).padStart(2, "0")}`;
  };

  return (
    <section
      data-testid="storyboard-assemble-panel"
      className="border border-yellow-400/30 bg-yellow-400/[0.04] rounded-sm p-5 space-y-3"
    >
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-yellow-400">
            Final video
          </h2>
          <p className="text-xs text-zinc-400 mt-1 max-w-xl">
            {stillsOnly ? (
              <>
                Stills-only channel — the final MP4 is assembled by
                <span className="text-yellow-300 font-bold"> Ken-Burns pan/zoom </span>
                over each scene&apos;s image, timed to the narration
                audio. No approved video takes are needed; every scene
                just needs its image generated.
              </>
            ) : (
              <>
                Stitches only the scene takes you&apos;ve <span className="text-emerald-300 font-bold">★ Approved</span> into ONE MP4 with horizontal-blur crossfades. Approve at least 2 takes to unlock production assembly. Each render is kept in the version history — pick the best take per scene.
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="storyboard-assemble-button"
          onClick={fire}
          disabled={submitting || status === "running" || !enoughApproved}
          title={
            !enoughApproved
              ? (stillsOnly
                  ? `Generate an image on every scene before assembling — ${stillsReadyCount}/${scenes.length} done`
                  : `Approve at least ${MIN_APPROVED} scene takes before stitching`)
              : status === "ready"
                ? (stillsOnly ? "Re-assemble the slideshow" : "Re-stitch with the current approved takes")
                : (stillsOnly ? "Assemble the still-slideshow into one MP4" : "Stitch the approved takes into one MP4")
          }
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-4 py-2 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {status === "running"
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {stillsOnly ? "assembling" : "stitching"} · {fmtMMSS(assembleElapsedSec)}</>
            : status === "ready"
              ? <><Wand2 className="w-3.5 h-3.5" /> {stillsOnly ? "Re-assemble" : "Re-stitch"}</>
              : <><Wand2 className="w-3.5 h-3.5" />{submitting ? "starting…" : (stillsOnly ? `Assemble slideshow (${stillsReadyCount}/${scenes.length})` : `Stitch approved (${approvedCount})`)}</>
          }
        </button>
      </header>

      <div
        data-testid="storyboard-approved-count"
        className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-400"
      >
        {stillsOnly ? (
          <>
            Scenes with images:&nbsp;
            <span className={enoughApproved ? "text-emerald-300 font-bold" : "text-yellow-300 font-bold"}>
              {stillsReadyCount}
            </span>
            <span className="text-zinc-600"> / {scenes.length} needed</span>
          </>
        ) : (
          <>
            Approved takes:&nbsp;
            <span className={enoughApproved ? "text-emerald-300 font-bold" : "text-yellow-300 font-bold"}>
              {approvedCount}
            </span>
            <span className="text-zinc-600"> / {MIN_APPROVED} min</span>
          </>
        )}
      </div>

      <PostFormatToggle
        storyboardId={board.storyboard_id}
        initial={(board.post_format || "video")}
        disabled={status === "running"}
      />

      <VoicePicker
        storyboardId={board.storyboard_id}
        initial={(board.voice_id || "")}
        disabled={status === "running"}
      />

      {(board.source_story || board.suggested_seo_description) && (
        <SourceStoryPanel
          storyboardId={board.storyboard_id}
          sourceStory={board.source_story || ""}
          suggestion={board.suggested_seo_description || ""}
          disabled={status === "running"}
        />
      )}

      {stillsOnly && (
        <StillsMotionToggle
          storyboardId={board.storyboard_id}
          initial={(board.stills_motion || "static")}
          disabled={status === "running"}
        />
      )}

      <StoryboardBumpersRow storyboardId={board.storyboard_id} />

      {!enoughApproved && status !== "ready" && (
        <div className="text-[11px] text-zinc-500 font-mono">
          {scenes.length === 0
            ? "Add a scene to get started."
            : stillsOnly
              ? `Generate an image on ${scenes.length - stillsReadyCount} more scene${scenes.length - stillsReadyCount === 1 ? "" : "s"} — click ‹Create Image› on any scene that still shows a placeholder.`
              : `Approve ${MIN_APPROVED - approvedCount} more scene take${MIN_APPROVED - approvedCount === 1 ? "" : "s"} — click the ★ Approve button next to the version you want shipped.`}
        </div>
      )}

      {status === "running" && (
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
              ↻ Stitching
            </div>
            <div
              data-testid="storyboard-assemble-elapsed"
              className="font-mono text-2xl tabular-nums text-yellow-100 font-bold"
            >
              {fmtMMSS(assembleElapsedSec)}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              · {(() => {
                // Honest estimate based on the sequential-pairwise
                // encode budget. N ≤ 3 → single-pass, snappy. N > 3
                // → (N-1) sequential 2-input encodes ~60s each on
                // production pod hardware.
                const n = scenes.length;
                if (n <= 3) return "usually under 1:00";
                const secs = (n - 1) * 60;
                const mm = Math.floor(secs / 60);
                return `usually ${mm}–${mm + 5} min for ${n} clips`;
              })()}
            </div>
            {/* Force-reset ONLY when there's real evidence the task
                is dead — i.e., the heartbeat is stale. If the task
                is actively encoding (green heartbeat), resetting
                throws away perfectly good work. `heartbeatStale`
                comes from the same rule the StitchHeartbeat pill
                uses (>45s = dead). */}
            {(() => {
              const hb = board?.assembled_heartbeat_at;
              const hbAge = hb
                ? Math.floor((Date.now() - new Date(hb).getTime()) / 1000)
                : Infinity;
              const looksDead = hbAge > 45 || (
                // Fallback: if there's no heartbeat at all AND the
                // job has been "running" for >2 min, offer reset.
                !hb && assembleElapsedSec > 120
              );
              if (!looksDead) return null;
              return (
                <button
                  type="button"
                  data-testid="storyboard-stitch-reset-btn"
                  onClick={async () => {
                    if (!window.confirm(
                      `Force-reset this stitch job?\n\n` +
                      `The task hasn't sent a heartbeat in ${hbAge}s (should be every 5s). ` +
                      `The pod almost certainly restarted or ffmpeg was killed. ` +
                      `Reset clears the lock so you can retry immediately.`,
                    )) return;
                    try {
                      await resetStoryboardAssemble(board.storyboard_id);
                      toast.success("Stitch lock cleared. You can retry now.");
                      onChanged?.();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Couldn't reset stitch");
                    }
                  }}
                  className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-red-300 border border-red-500/50 hover:bg-red-500/10 px-3 py-1.5 rounded-sm transition-colors"
                >
                  ✕ Force reset stitch
                </button>
              );
            })()}
          </div>
          <div className="text-[11px] text-zinc-400 font-mono">
            Stitching {scenes.length} scene{scenes.length === 1 ? "" : "s"}. This page refreshes automatically.
          </div>
          {/* Live proof-of-life heartbeat. The backend writes
              `assembled_heartbeat_at` every ~15s during the stitch.
              If that timestamp stops advancing for more than 60s,
              the task is almost certainly dead. Showing this age
              lets the user (and me during debugging) instantly tell
              "genuinely encoding" from "silently orphaned". */}
          <StitchHeartbeat
            heartbeatIso={board?.assembled_heartbeat_at}
            startedIso={board?.assembled_started_at}
            stage={board?.assembled_stage}
          />
        </div>
      )}

      {status === "failed" && (
        <div className="text-[11px] text-red-300 font-mono leading-relaxed">
          Assembly failed: {error || "ffmpeg reported an error"}.
          {enoughApproved && " You can hit Stitch approved to retry."}
        </div>
      )}

      {status === "ready" && url && (
        <div className="space-y-3">
          <video
            data-testid="storyboard-assembled-video"
            src={url}
            controls
            playsInline
            className="w-full max-w-3xl aspect-video bg-black border border-zinc-800 rounded-sm"
          />
          <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            <span>{sceneCount} scene{sceneCount === 1 ? "" : "s"}</span>
            {durSec > 0 && <span>· {fmtDur(durSec)} duration</span>}
            <span>· hblur crossfade</span>
            <button
              type="button"
              onClick={downloadFinal}
              data-testid="storyboard-assembled-download"
              className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-3 py-1.5 rounded-sm transition-colors"
            >
              <Download className="w-3 h-3" /> Download MP4
            </button>
          </div>
          <PublishToYouTubeBar board={board} onPublished={onChanged} />
        </div>
      )}
    </section>
  );
}

/**
 * Stills-mode motion style picker — only rendered when the storyboard's
 * `post_format` is "stills". Lets the user choose between:
 *   • Ken Burns pan/zoom (cinematic default)
 *   • Static hard-cut slideshow (no motion, ~5× cheaper CPU-wise)
 * Persists the choice via PATCH so the next Assemble reads it.
 */
function StillsMotionToggle({ storyboardId, initial = "static", disabled = false }) {
  const [motion, setMotion] = useState(initial);
  const [saving, setSaving] = useState(false);

  const setValue = async (val) => {
    if (val === motion || saving || disabled) return;
    const prev = motion;
    setMotion(val);  // optimistic
    setSaving(true);
    try {
      await updateStoryboardMeta(storyboardId, { stillsMotion: val });
      toast.success(
        val === "kenburns"
          ? "Ken-Burns motion enabled — scenes will slowly pan and zoom."
          : "Static slideshow — no motion, hard cuts between scenes.",
      );
    } catch (e) {
      setMotion(prev);  // rollback
      toast.error(e?.response?.data?.detail || "Couldn't save motion style");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="stills-motion-toggle"
      className="flex flex-wrap items-center gap-3 border border-zinc-800 bg-black/40 rounded-sm p-3"
    >
      <div className="min-w-0">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400">
          Motion style
        </div>
        <div className="text-[11px] text-zinc-400 leading-relaxed max-w-md">
          Ken-Burns adds a slow pan/zoom to each still. Static uses hard cuts —
          faster to render and better for text-heavy diagrams.
        </div>
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        <MotionChip
          active={motion === "kenburns"}
          label="Ken Burns"
          testid="stills-motion-kenburns"
          disabled={disabled || saving}
          onClick={() => setValue("kenburns")}
        />
        <MotionChip
          active={motion === "static"}
          label="Static"
          testid="stills-motion-static"
          disabled={disabled || saving}
          onClick={() => setValue("static")}
        />
      </div>
    </div>
  );
}

function MotionChip({ active, label, testid, disabled, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={
        active
          ? "no-pill font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-sm disabled:opacity-50 disabled:cursor-wait"
          : "font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-3 py-1.5 rounded-sm disabled:opacity-50 disabled:cursor-wait transition-colors"
      }
    >
      {label}
    </button>
  );
}


/**
 * Full-screen scene image lightbox — opens when the user clicks a
 * rendered scene thumbnail on the storyboard detail page.
 *
 * Rendered inline (not through shadcn Dialog) so it can go
 * edge-to-edge on the viewport — the whole point of "full size" is
 * to escape Dialog's max-width constraint. Locks body scroll while
 * open. Closes on Escape, on backdrop click, or on the X button.
 * Also offers Open-in-new-tab for cases where the user wants the
 * raw image URL in the browser (e.g. right-click → save-as).
 */
function SceneImageLightbox({ src, alt, sceneIdx, onClose }) {
  useEffect(() => {
    // Body-scroll lock so the underlying storyboard doesn't scroll
    // behind the fixed backdrop. `overflow: hidden` is the cheapest
    // trick that works across all mainstream browsers.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      data-testid={`scene-lightbox-${sceneIdx}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Full-size view of ${alt}`}
      onClick={(e) => {
        // React 18 propagates synthetic events through the React
        // virtual tree, NOT the DOM tree — so even though the
        // portal moves this <div> to document.body, this onClick
        // still synthetically bubbles up to any onClick on the
        // React parent (the tile <button> that opened us). Halt
        // that bubble here or the tile re-opens the lightbox
        // immediately after we close it.
        e.stopPropagation();
        onClose();
      }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 sm:p-8 cursor-zoom-out"
    >
      {/* Stop-propagation wrapper so clicks on the image itself
          don't close the overlay — only the surrounding backdrop
          does. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-full max-h-full flex flex-col items-center gap-3 cursor-default"
      >
        <img
          data-testid={`scene-lightbox-image-${sceneIdx}`}
          src={src}
          alt={alt}
          className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl shadow-black/80 border border-zinc-800"
        />
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">
          <span className="text-yellow-400/80">{alt}</span>
          <span className="text-zinc-600">·</span>
          <a
            href={src}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`scene-lightbox-open-tab-${sceneIdx}`}
            className="inline-flex items-center gap-1 hover:text-yellow-200 hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            open in new tab
          </a>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500">Esc to close</span>
        </div>
      </div>
      <button
        type="button"
        data-testid={`scene-lightbox-close-${sceneIdx}`}
        onClick={(e) => {
          // Extra safety: even though the portal lifts us out of
          // the tile <button> subtree, stop propagation so any
          // future ancestor click handler can't interpret this
          // close as a re-open.
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close full-size view"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/70 hover:bg-black text-white border border-zinc-700 hover:border-yellow-400 flex items-center justify-center transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
    </div>,
    document.body,
  );
}


/**
 * Per-storyboard ElevenLabs voice picker. Wired the same way as
 * ChannelSettingsPanel's voice select but scoped to a single
 * storyboard: PATCH /storyboards/{sid} with { voice_id }. Empty
 * string means "let the assembler fall back to the channel default
 * / Rachel". Voices are fetched once on mount via listVoices() and
 * grouped (built-in / custom / resemble) to mirror the Channel Kit
 * picker exactly — familiar UX, zero drift.
 */
function VoicePicker({ storyboardId, initial = "", disabled = false }) {
  const [voiceId, setVoiceId] = useState(initial);
  const [voices, setVoices] = useState({ builtin: [], custom: [], resemble: [] });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    listVoices()
      .then((res) => {
        if (!alive) return;
        setVoices({
          builtin: res?.builtin || [],
          custom: res?.custom || [],
          resemble: res?.resemble || [],
        });
      })
      .catch(() => {
        /* leave defaults empty — the picker still renders with the
           "Auto" option so the user isn't blocked. */
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  const currentLabel = (() => {
    if (!voiceId) return "Auto";
    const all = [
      ...(voices.builtin || []),
      ...(voices.custom || []),
      ...(voices.resemble || []),
    ];
    return all.find((v) => v.voice_id === voiceId)?.label || voiceId;
  })();

  const onChange = async (val) => {
    if (val === voiceId || saving || disabled) return;
    const prev = voiceId;
    setVoiceId(val);  // optimistic
    setSaving(true);
    try {
      await updateStoryboardMeta(storyboardId, { voiceId: val });
      toast.success(
        val
          ? `Voice updated. Regenerate voice-overs to hear it — the current MP3 (if any) still uses the old voice.`
          : `Voice reset to Auto — the assembler will use the channel default (or Rachel as ultimate fallback).`,
      );
    } catch (e) {
      setVoiceId(prev);  // rollback
      toast.error(e?.response?.data?.detail || "Couldn't save voice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="storyboard-voice-picker"
      data-current-voice={voiceId || ""}
      className="flex flex-wrap items-center gap-3 border border-zinc-800 bg-black/40 rounded-sm p-3"
    >
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400">
          Narration voice
        </div>
        <div className="text-[11px] text-zinc-400 leading-relaxed max-w-lg">
          The ElevenLabs voice used for the final voice-over on this
          board.{" "}
          <span className="text-zinc-500">
            Currently: <span className="text-zinc-300">{currentLabel}</span>
          </span>
          . Changing it here doesn&apos;t re-render the existing MP3 —
          hit &laquo;Regenerate voice-overs&raquo; after changing.
        </div>
      </div>
      <select
        data-testid="storyboard-voice-select"
        value={voiceId}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || saving || !loaded}
        className="min-w-[220px] bg-black border border-zinc-700 hover:border-yellow-400/60 focus:border-yellow-400 text-sm text-zinc-100 font-mono px-3 py-2 rounded-sm focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-wait"
      >
        <option value="">Auto (channel default / Rachel)</option>
        {(() => {
          // If the storyboard's saved voice_id doesn't match any voice
          // returned by /api/auto-video-voices (e.g. the Resemble
          // library hasn't loaded yet, or the user's Resemble token
          // expired), the <select> would silently render "Auto" and
          // the user would think the value was reset. Render a
          // fallback option so the SAVED value stays visible and
          // selected until they explicitly change it.
          const known = new Set([
            ...voices.builtin.map((v) => v.voice_id),
            ...voices.custom.map((v) => v.voice_id),
            ...voices.resemble.map((v) => v.voice_id),
          ]);
          if (!voiceId || known.has(voiceId)) return null;
          const label = voiceId.startsWith("rsm:")
            ? `Resemble · ${voiceId.slice(4, 12)}`
            : voiceId;
          return (
            <option value={voiceId}>
              {`${label} (saved)`}
            </option>
          );
        })()}
        {voices.builtin.length > 0 && (
          <optgroup label="Built-in voices">
            {voices.builtin.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>
                {v.label}
              </option>
            ))}
          </optgroup>
        )}
        {voices.custom.length > 0 && (
          <optgroup label="My voices (ElevenLabs library)">
            {voices.custom.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>
                {v.label}
                {v.category ? ` · ${v.category}` : ""}
              </option>
            ))}
          </optgroup>
        )}
        {voices.resemble.length > 0 && (
          <optgroup label="Resemble voices">
            {voices.resemble.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>
                {v.label}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      <VoiceSamplePlayButton
        voiceId={voiceId}
        testid="storyboard-voice-sample-play"
      />
    </div>
  );
}


/**
 * "Your Story" panel — displays the raw plot text the user typed
 * into Create Your Own Story, plus (optionally) the AI's suggested
 * YouTube-ready SEO version they can promote into the actual
 * YouTube Description with one click.
 *
 * Shown only when at least one of the two fields is populated
 * (i.e. this storyboard was created via Custom Story). Regular
 * Content-Factory boards don't have a `source_story` so they'll
 * never render this panel — keeps the detail page uncluttered for
 * the majority workflow.
 */
function SourceStoryPanel({ storyboardId, sourceStory, suggestion, disabled }) {
  const [story, setStory] = useState(sourceStory || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  const save = async () => {
    if (saving || disabled) return;
    const next = story.trim();
    if (!next) {
      toast.error("Story can't be empty");
      return;
    }
    setSaving(true);
    try {
      await updateStoryboardMeta(storyboardId, { sourceStory: next });
      toast.success("Story saved");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save story");
    } finally {
      setSaving(false);
    }
  };

  const useSuggestion = async () => {
    if (applying || disabled || !suggestion) return;
    if (!window.confirm(
      "Replace the YouTube Description with the AI's SEO-optimized version? "
      + "You can still edit it after.",
    )) return;
    setApplying(true);
    try {
      await updateStoryboardMeta(storyboardId, { seoDescription: suggestion });
      toast.success("YouTube Description updated with the SEO version. Reloading…");
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply the suggestion");
    } finally {
      setApplying(false);
    }
  };

  return (
    <section
      data-testid="source-story-panel"
      className="border border-zinc-800 bg-black/40 rounded-sm p-4 space-y-3"
    >
      <div className="flex items-baseline gap-3">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400">
          // Your Story
        </div>
        <div className="text-[11px] text-zinc-500">
          The plot you typed into Create Your Own Story — preserved
          verbatim.
        </div>
        {!editing && sourceStory && (
          <button
            type="button"
            data-testid="source-story-edit"
            onClick={() => setEditing(true)}
            disabled={disabled}
            className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border border-zinc-700 hover:border-yellow-400/60 hover:text-yellow-200 text-zinc-300 rounded-sm transition-colors disabled:opacity-50"
          >
            edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            data-testid="source-story-textarea"
            value={story}
            onChange={(e) => setStory(e.target.value.slice(0, 6000))}
            rows={7}
            className="w-full bg-black border border-zinc-700 focus:border-yellow-400 outline-none rounded-sm px-3 py-2 text-sm text-zinc-200 font-mono leading-relaxed"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="source-story-save"
              onClick={save}
              disabled={saving || disabled}
              className="font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-sm disabled:opacity-50 disabled:cursor-wait"
            >
              {saving ? "saving…" : "save story"}
            </button>
            <button
              type="button"
              data-testid="source-story-cancel"
              onClick={() => {
                setStory(sourceStory || "");
                setEditing(false);
              }}
              disabled={saving}
              className="font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-3 py-1.5 rounded-sm disabled:opacity-50"
            >
              cancel
            </button>
          </div>
        </div>
      ) : sourceStory ? (
        <div
          data-testid="source-story-body"
          className="text-sm text-zinc-200 font-mono leading-relaxed whitespace-pre-wrap"
        >
          {sourceStory}
        </div>
      ) : (
        <div className="text-[11px] text-zinc-500 font-mono italic">
          // no raw story recorded for this board — this section only
          appears for boards created via Create Your Own Story
        </div>
      )}

      {suggestion && (
        <div className="border-t border-zinc-800 pt-3 space-y-2">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-400">
            // AI-suggested SEO version
          </div>
          <div
            data-testid="source-story-suggestion-body"
            className="text-[12px] text-zinc-400 leading-relaxed whitespace-pre-wrap max-h-40 overflow-auto"
          >
            {suggestion}
          </div>
          <button
            type="button"
            data-testid="source-story-apply-suggestion"
            onClick={useSuggestion}
            disabled={applying || disabled}
            className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-emerald-500 hover:bg-emerald-500 hover:text-black text-emerald-300 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-wait"
          >
            {applying ? "applying…" : "use as YouTube Description →"}
          </button>
        </div>
      )}
    </section>
  );
}


/**
 * Per-storyboard override of the channel-wide post_format setting.
 * Sits above the StillsMotionToggle so users can flip THIS board
 * between Video / Stills without touching Channel Settings. When
 * the user flips to Stills, the StillsMotionToggle animates into
 * view underneath (its own render is gated on `stillsOnly` upstream).
 *
 * The optimistic update flips local state first and rolls back on
 * error — same pattern as StillsMotionToggle. We also call
 * `window.location.reload()` after a successful flip so the board
 * doc re-fetches with the new `stillsOnly` derivation and
 * downstream UI (scene cards, status pills, publish bar) rebuilds
 * against the correct mode.
 */
function PostFormatToggle({ storyboardId, initial = "video", disabled = false }) {
  const [format, setFormat] = useState(initial);
  const [saving, setSaving] = useState(false);

  const setValue = async (val) => {
    if (val === format || saving || disabled) return;
    const prev = format;
    setFormat(val);  // optimistic
    setSaving(true);
    try {
      await updateStoryboardMeta(storyboardId, { postFormat: val });
      toast.success(
        val === "stills"
          ? "Switched to Stills — scenes will render as images and stitch into a slideshow. Per-scene video renders are skipped."
          : "Switched to Video — every scene will render a preview MP4 (Kling / Seedance / etc).",
      );
      // Reload so the board doc re-fetches under the new mode and
      // every downstream derivation (StillsMotionToggle visibility,
      // Publish bar assembly copy, scene-card CTA text) rebuilds
      // against the fresh post_format value.
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      setFormat(prev);  // rollback
      toast.error(e?.response?.data?.detail || "Couldn't save post format");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="post-format-toggle"
      data-current-format={format}
      className="flex flex-wrap items-center gap-3 border border-zinc-800 bg-black/40 rounded-sm p-3"
    >
      <div className="min-w-0">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400">
          Post format
        </div>
        <div className="text-[11px] text-zinc-400 leading-relaxed max-w-md">
          Video = every scene renders a preview MP4 (Kling / Seedance).
          Stills = scenes stay as images, final video is a slideshow
          with narration — skips per-scene video renders entirely.
        </div>
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        <MotionChip
          active={format === "video"}
          label="Video"
          testid="post-format-video"
          disabled={disabled || saving}
          onClick={() => setValue("video")}
        />
        <MotionChip
          active={format === "stills"}
          label="Stills"
          testid="post-format-stills"
          disabled={disabled || saving}
          onClick={() => setValue("stills")}
        />
      </div>
    </div>
  );
}

/**
 * PublishToYouTubeBar — one-click direct upload of the stitched
 * storyboard MP4 to the user's linked YouTube channel. Only rendered
 * once the assembled MP4 exists (parent already gates on
 * `status === "ready"`).
 *
 * Defaults to `privacy_status="private"` so a mis-click never goes
 * live. User picks private / unlisted / public in the dropdown before
 * hitting publish, and can flip visibility later from YT Studio.
 * Once uploaded, we show a persistent "posted → watch on YT" chip
 * with a link to the video.
 */
function PublishToYouTubeBar({ board, onPublished }) {
  const [visibility, setVisibility] = useState("private");
  const [publishing, setPublishing] = useState(false);
  const posted = !!board?.youtube_video_id;

  const onPublish = async () => {
    if (publishing) return;
    const confirmMsg = visibility === "public"
      ? `Publish "${board?.video_name || "this video"}" PUBLICLY to YouTube right now?`
      : `Upload "${board?.video_name || "this video"}" to YouTube as ${visibility}?`;
    if (!window.confirm(confirmMsg)) return;
    setPublishing(true);
    try {
      const res = await publishStoryboardToYouTube(board.storyboard_id, {
        privacyStatus: visibility,
      });
      toast.success(
        `Uploaded to YouTube (${visibility}) — video_id ${res.video_id || "…"}`,
      );
      onPublished?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(String(detail).slice(0, 400));
    } finally {
      setPublishing(false);
    }
  };

  if (posted) {
    return (
      <div
        data-testid="storyboard-yt-posted-chip"
        className="border border-emerald-400/40 bg-emerald-400/5 rounded-sm p-3 flex items-center gap-3 flex-wrap"
      >
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-300">
          ▸ posted to youtube
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          · {board.youtube_privacy_status || "private"}
        </span>
        <a
          href={board.youtube_watch_url}
          target="_blank"
          rel="noreferrer"
          className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-3 py-1.5 rounded-sm transition-colors"
        >
          <ExternalLink className="w-3 h-3" /> Watch on YouTube
        </a>
        <button
          type="button"
          onClick={onPublish}
          disabled={publishing}
          data-testid="storyboard-yt-repost-btn"
          title="Upload this stitched MP4 to YouTube again"
          className="no-pill inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-zinc-100 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        >
          {publishing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {publishing ? "uploading…" : "Re-upload"}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="storyboard-yt-publish-bar"
      className="border border-yellow-400/40 bg-yellow-400/5 rounded-sm p-3 flex items-center gap-3 flex-wrap"
    >
      <div className="min-w-0">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-400">
          ▸ post to youtube
        </div>
        <div className="text-[11px] text-zinc-400 max-w-md leading-relaxed">
          Upload this stitched MP4 directly to your linked YouTube channel.
          Defaults to <span className="text-yellow-300">private</span> so you
          can review + set thumbnail in Studio before going live.
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
          <span>visibility</span>
          <select
            data-testid="storyboard-yt-visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            disabled={publishing}
            className="no-pill bg-black border border-zinc-800 hover:border-yellow-400/60 focus:border-yellow-400 text-[11px] text-zinc-100 font-mono px-2 py-1.5 rounded-sm focus:outline-none"
          >
            <option value="private">private</option>
            <option value="unlisted">unlisted</option>
            <option value="public">public</option>
          </select>
        </label>
        <button
          type="button"
          onClick={onPublish}
          disabled={publishing}
          data-testid="storyboard-yt-publish-btn"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 hover:bg-yellow-300 text-black font-black px-4 py-2 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {publishing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          {publishing ? "Uploading…" : "Post to YouTube"}
        </button>
      </div>
    </div>
  );
}



function ThumbnailHeroPanel({ board, onChanged }) {
  // Hero thumbnail rendered under the video title. Auto-generated
  // when the storyboard is created (via the backend background
  // task). The user can Regenerate via ChatGPT (gpt-image-1) with
  // an inline character picker + revision notes, or Replace with
  // their own upload.
  //
  // Aspect note: gpt-image-1's smallest 16:9-ish output is
  // 1536×1024 (~3:2). We render inside a 3/2 container using
  // `object-contain` so the whole image is visible without top/
  // bottom clipping. YouTube itself accepts 1536×1024 fine and
  // will letterbox for 16:9 display in-player.
  const url = (board?.thumbnail_url || "").trim();
  const [busy, setBusy] = useState("");
  const [imgError, setImgError] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setImgError(false); }, [url]);

  // Called either directly (no-cast-change quick regen) or from
  // the customization dialog with `{ characterIds, notes }`.
  const runRegenerate = useCallback(
    async (opts = {}) => {
      if (busy) return;
      setBusy("regen");
      try {
        await regenerateStoryboardThumbnail(board.storyboard_id, opts);
        toast.success("ChatGPT rendered a fresh thumbnail");
        onChanged?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't regenerate thumbnail");
        throw e;
      } finally {
        setBusy("");
      }
    },
    [board?.storyboard_id, busy, onChanged],
  );

  const onUploadClick = () => fileRef.current?.click();
  const onSpellcheckThumb = async () => {
    if (busy) return;
    setBusy("spellcheck");
    try {
      const res = await spellcheckThumbnail(board.storyboard_id);
      toast.success(
        res?.clean
          ? "No spelling issues found"
          : `Found ${res?.issues?.length || 0} spelling issue(s)`,
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spell check failed");
    } finally {
      setBusy("");
    }
  };
  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (busy) return;
    setBusy("upload");
    try {
      await uploadStoryboardThumbnail(board.storyboard_id, f);
      toast.success("Thumbnail replaced");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDownload = async () => {
    if (!url) return;
    try {
      const res = await fetch(imgUrl(url));
      if (!res.ok) throw new Error(`http_${res.status}`);
      const blob = await res.blob();
      const safeName = (board.video_name || "thumbnail").replace(/[/\\:*?"<>|]/g, "_").slice(0, 80);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${safeName}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 500);
    } catch (e) {
      toast.error("Couldn't download thumbnail");
    }
  };

  const isRendering = busy === "regen";
  return (
    <div
      data-testid="storyboard-thumbnail-panel"
      className="mt-3 border border-zinc-900 rounded-sm bg-zinc-950 overflow-hidden"
    >
      {regenOpen && (
        <ThumbnailRegenerateDialog
          board={board}
          onClose={() => setRegenOpen(false)}
          onConfirm={runRegenerate}
        />
      )}
      <div className="aspect-[3/2] relative bg-black">
        {isRendering && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/70">
            <Loader2 className="w-7 h-7 text-yellow-400 animate-spin" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-300 font-bold">
              ChatGPT rendering thumbnail…
            </span>
          </div>
        )}
        {url && !imgError ? (
          <img
            data-testid="storyboard-thumbnail-img"
            src={imgUrl(url)}
            alt={`Thumbnail for ${board.video_name}`}
            onError={() => setImgError(true)}
            className="absolute inset-0 w-full h-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {imgError ? "thumbnail couldn't load" : "no thumbnail yet"}
            </span>
            <span className="text-[11px] text-zinc-600 font-mono">
              Click <span className="text-emerald-300">Generate</span> to render
              one via ChatGPT
            </span>
          </div>
        )}
      </div>
      {(board?.thumbnail_spellcheck || url) && (
        <div className="px-3 pt-2 flex flex-wrap items-center gap-2">
          <SpellcheckBadge
            spellcheck={board?.thumbnail_spellcheck}
            busy={!!busy}
            onFix={(note) => runRegenerate({ notes: note })}
            testidPrefix="storyboard-thumbnail-spellcheck"
          />
          {url && (
            <button
              type="button"
              data-testid="storyboard-thumbnail-spellcheck-run"
              onClick={onSpellcheckThumb}
              disabled={!!busy}
              title="Proofread the thumbnail's on-image text with a vision LLM"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-yellow-400 disabled:opacity-40 transition-colors"
            >
              {busy === "spellcheck" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              {busy === "spellcheck" ? "checking text…" : board?.thumbnail_spellcheck ? "re-check text" : "check text spelling"}
            </button>
          )}
        </div>
      )}
      <div className="px-3 py-2 border-t border-zinc-900 flex items-center justify-between gap-2 flex-wrap">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">
          // youtube thumbnail · 1536×1024 · gpt-image-1
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="storyboard-thumbnail-regenerate-btn"
            onClick={() => (url ? setRegenOpen(true) : runRegenerate())}
            disabled={!!busy}
            title={url ? "Customize cast + notes, then regenerate" : "Generate via ChatGPT (gpt-image-1)"}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 bg-emerald-400/5 hover:bg-emerald-400/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy === "regen"
              ? <><Loader2 className="w-3 h-3 animate-spin" /> rendering…</>
              : <><Wand2 className="w-3 h-3" /> {url ? "customize + regenerate" : "generate"}</>}
          </button>
          <button
            type="button"
            data-testid="storyboard-thumbnail-upload-btn"
            onClick={onUploadClick}
            disabled={!!busy}
            title="Replace with an uploaded PNG/JPG"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-yellow-400/60 text-zinc-300 hover:text-yellow-200 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
          >
            {busy === "upload"
              ? <><Loader2 className="w-3 h-3 animate-spin" /> uploading…</>
              : <><Upload className="w-3 h-3" /> upload</>}
          </button>
          <input
            ref={fileRef}
            data-testid="storyboard-thumbnail-upload-input"
            type="file"
            accept="image/png,image/jpeg"
            onChange={onFileChosen}
            className="hidden"
          />
          {url && (
            <button
              type="button"
              data-testid="storyboard-thumbnail-download-btn"
              onClick={onDownload}
              disabled={!!busy}
              title="Download as PNG"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-cyan-400/60 text-zinc-300 hover:text-cyan-200 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
            >
              <Download className="w-3 h-3" /> download
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text, label = "copy", testid, className = "" }) {
  // Tiny pill that copies the given text to the clipboard and
  // briefly flips its icon to a checkmark so the user can see the
  // copy fired. Uses the modern navigator.clipboard API with a
  // text-area fallback for older browsers.
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    const value = String(text || "");
    if (!value) {
      toast.error("Nothing to copy");
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Copy failed — select and ⌘C / Ctrl+C");
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      title="Copy to clipboard"
      className={
        "inline-flex items-center gap-1 px-2 py-1 rounded-sm font-mono text-[10px] uppercase tracking-[0.25em] border transition-colors " +
        (copied
          ? "text-emerald-200 border-emerald-400/70 bg-emerald-400/10"
          : "text-zinc-500 hover:text-yellow-300 border-zinc-800 hover:border-yellow-400/50") +
        " " + className
      }
    >
      {copied
        ? <><Check className="w-3 h-3" /> copied</>
        : <><Copy className="w-3 h-3" /> {label}</>
      }
    </button>
  );
}

function VideoDescriptionPanel({ board, onChanged }) {
  // SEO-optimized YouTube description that goes under the video on
  // upload. Auto-drafted by GPT-5.2 from the storyboard's title +
  // scene summaries + channel kit; fully editable; one-click copy
  // to paste into the YouTube Studio description box.
  const existing = (board?.seo_description || "").trim();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(existing);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(existing); }, [existing]);

  const onGenerate = async () => {
    if (generating) return;
    if (existing && !window.confirm(
      "Replace the current description with a freshly generated one?",
    )) return;
    setGenerating(true);
    try {
      const res = await generateStoryboardDescription(board.storyboard_id);
      toast.success("ChatGPT drafted a fresh SEO description");
      setDraft(res?.description || "");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate description");
    } finally {
      setGenerating(false);
    }
  };

  const onSaveEdit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await updateStoryboardMeta(board.storyboard_id, { seoDescription: draft });
      toast.success("Description saved");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="storyboard-description-panel"
      className="mt-3 border border-zinc-900 rounded-sm bg-zinc-950 p-4 space-y-3"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
            // youtube description
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5 font-mono">
            SEO-optimized for YouTube · paste straight into the upload form
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {existing && (
            <CopyButton
              text={existing}
              label="copy description"
              testid="storyboard-copy-description-btn"
            />
          )}
          {!editing && (
            <button
              type="button"
              data-testid="storyboard-edit-description-btn"
              onClick={() => { setDraft(existing); setEditing(true); }}
              title={existing
                ? "Edit the current description"
                : "Paste or type your own description"}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-yellow-300 border border-zinc-800 hover:border-yellow-400/50 px-2 py-1 rounded-sm"
            >
              {existing ? "edit" : "paste manually"}
            </button>
          )}
          <button
            type="button"
            data-testid="storyboard-generate-description-btn"
            onClick={onGenerate}
            disabled={generating}
            title={existing
              ? "Replace with a fresh ChatGPT-drafted description"
              : "Draft a YouTube description via ChatGPT"}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 bg-emerald-400/5 hover:bg-emerald-400/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {generating
              ? <><Loader2 className="w-3 h-3 animate-spin" /> drafting…</>
              : <><Wand2 className="w-3 h-3" /> {existing ? "regenerate" : "generate via chatgpt"}</>}
          </button>
        </div>
      </header>

      {editing ? (
        <div className="space-y-2">
          <textarea
            data-testid="storyboard-description-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={14}
            maxLength={5000}
            placeholder="Paste or type your YouTube description here…"
            className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 leading-relaxed font-mono focus:border-emerald-400/60 focus:outline-none"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-mono text-[10px] text-zinc-600">
              {draft.length} / 5000
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setDraft(existing); setEditing(false); }}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 px-2 py-1.5 rounded-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="storyboard-description-save-btn"
                onClick={onSaveEdit}
                disabled={saving}
                className="font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-bold px-3 py-1.5 rounded-sm disabled:opacity-50"
              >
                {saving ? "saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : existing ? (
        <pre
          data-testid="storyboard-description-text"
          className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap font-sans m-0"
        >
          {existing}
        </pre>
      ) : (
        <button
          type="button"
          data-testid="storyboard-description-empty-cta"
          onClick={() => { setDraft(""); setEditing(true); }}
          className="w-full text-left text-xs text-zinc-500 font-mono leading-relaxed hover:text-zinc-300 transition-colors cursor-text"
        >
          // no description yet — click <span className="text-yellow-300">paste manually</span>{" "}
          to paste your own, or <span className="text-emerald-300">generate via chatgpt</span>{" "}
          to draft a 4-paragraph SEO-optimized blurb (story → value-prop →
          audience → reminder → hashtags) you can paste into YouTube.
        </button>
      )}
    </div>
  );
}

function VideoNameEditor({ board, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(board.video_name);
  useEffect(() => { setName(board.video_name); }, [board.video_name]);
  const save = async () => {
    try {
      await updateStoryboardMeta(board.storyboard_id, { videoName: name });
      toast.success("Renamed");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };
  if (!editing) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <h1
          data-testid="storyboard-video-name"
          className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight"
        >
          {board.video_name}
        </h1>
        <CopyButton
          text={board.video_name || ""}
          label="copy title"
          testid="storyboard-copy-title-btn"
        />
        <button
          type="button"
          data-testid="storyboard-rename-btn"
          onClick={() => setEditing(true)}
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-yellow-300 border border-zinc-800 hover:border-yellow-400/50 px-2 py-1 rounded-sm"
        >
          rename
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        data-testid="storyboard-rename-input"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="flex-1 max-w-md bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-lg text-zinc-100 font-bold"
      />
      <button
        type="button"
        onClick={save}
        data-testid="storyboard-rename-save"
        className="font-mono text-[10px] uppercase tracking-[0.25em] bg-yellow-400 text-black hover:bg-yellow-300 font-bold px-3 py-1.5 rounded-sm"
      >
        Save
      </button>
      <button
        type="button"
        onClick={() => { setName(board.video_name); setEditing(false); }}
        className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 px-2 py-1.5 rounded-sm"
      >
        Cancel
      </button>
    </div>
  );
}

function imgUrl(raw) {
  if (!raw) return "";
  if (raw.startsWith("http")) return raw;
  if (raw.startsWith("/")) return `${API_BASE}${raw}`;
  return raw;
}

async function downloadImageFromUrl(url, filename) {
  // Fetch as blob and trigger a download so the browser saves the
  // file instead of just navigating to (and inlining) it. The image
  // is same-origin (served from /api/uploads/...) so this just works.
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`download failed (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || "scene.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

const TEMPLATE_FIELDS = [
  { key: "description", label: "Scene Description", required: true, rows: 4,
    placeholder: "What happens in this scene? Setting, characters, action…" },
  { key: "dialog", label: "Dialog", required: false, rows: 3,
    placeholder: 'e.g. Bella says: "let\'s go camping!"  Benny laughs and says: "i can\'t wait!"' },
  { key: "audio", label: "Audio", required: false, rows: 2,
    placeholder: "Music, ambient SFX, sound design notes…" },
  { key: "camera", label: "Camera", required: false, rows: 2,
    placeholder: "e.g. slow push-in on Bella's face, then pan right" },
  { key: "lighting", label: "Lighting", required: false, rows: 2,
    placeholder: "e.g. warm golden-hour, soft fill, fairy lights on tent" },
];

function PromptBlock({
  title, hint, draft, setDraft, isOverride, autoPrompt, refImageUrls,
  onSave, onReset, onRegenerate, saving, rows = 6, testIdPrefix,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
            {title}
          </h3>
          <span
            data-testid={`${testIdPrefix}-status`}
            className={
              "font-mono text-[9px] uppercase tracking-[0.25em] " +
              (isOverride ? "text-amber-300" : "text-zinc-500")
            }
          >
            {isOverride ? "custom override" : "auto-built"}
          </span>
        </div>
        <p className="text-[10px] text-zinc-500 leading-relaxed max-w-md text-right">
          {hint}
        </p>
      </div>
      <textarea
        data-testid={`${testIdPrefix}-textarea`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={rows}
        spellCheck={false}
        className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-200 font-mono leading-relaxed focus:border-yellow-400/60 focus:outline-none resize-y"
      />
      {!isOverride && autoPrompt !== null && autoPrompt !== undefined && (
        <p className="text-[10px] text-zinc-600 font-mono leading-relaxed">
          this is the auto-built prompt. edit it and click Save to pin a custom override.
        </p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid={`${testIdPrefix}-save`}
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-yellow-400/60 hover:border-yellow-300 text-yellow-200 hover:text-yellow-100 px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save override
        </button>
        <button
          type="button"
          data-testid={`${testIdPrefix}-regenerate`}
          onClick={onRegenerate}
          disabled={saving}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-50"
        >
          <Wand2 className="w-3 h-3" />
          Save & regenerate
        </button>
        {isOverride && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-reset`}
            onClick={onReset}
            disabled={saving}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-zinc-200 px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-50"
          >
            <RefreshCcw className="w-3 h-3" />
            Reset to auto
          </button>
        )}
      </div>
      {refImageUrls && refImageUrls.length > 0 && (
        <div className="pt-2 mt-2 border-t border-zinc-900 space-y-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">
            attached reference images ({refImageUrls.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {refImageUrls.map((u, i) => (
              <a
                key={u + i}
                href={u}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-10 h-10 border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950 hover:border-yellow-400/60"
                title={u}
              >
                <img src={u} alt="ref" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function SceneCard({ scene, storyboardId, onChanged, stillsOnly = false }) {
  const [fields, setFields] = useState(() => ({
    description: scene.description || "",
    dialog: scene.dialog || "",
    audio: scene.audio || "",
    camera: scene.camera || "",
    lighting: scene.lighting || "",
  }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Synchronous lock for `onPreview` — see the comment in onPreview for
  // the full rationale on why a useState boolean isn't enough.
  const previewInFlightRef = useRef(false);
  const [correctionNote, setCorrectionNote] = useState("");
  // Per-scene video provider override. Empty string lets the backend
  // fall back to its default (Seedance 2.0). The dropdown next to the
  // render button writes here; `onPreview` passes it through.
  const [previewProvider, setPreviewProvider] = useState("");
  const [refUploading, setRefUploading] = useState(false);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsData, setPromptsData] = useState(null);
  const [imagePromptDraft, setImagePromptDraft] = useState("");
  const [videoPromptDraft, setVideoPromptDraft] = useState("");
  const [savingPrompts, setSavingPrompts] = useState(false);
  // Tracks whether `scene.image_url` actually loaded in the browser.
  // When the URL is set but the underlying GridFS blob is missing or
  // corrupt, the browser triggers <img> onError. Without this state
  // the user would just see a silent broken image — with it we swap
  // in the upload placeholder + a "Re-generate" hint per Support's
  // recommendation #1. Reset to false whenever the URL changes so a
  // freshly-regenerated image gets a clean shot at loading.
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  useEffect(() => { setImageLoadFailed(false); }, [scene.image_url]);
  // Full-size lightbox — opens when the user clicks a rendered
  // scene thumbnail. Kept as SceneCard-local state (not lifted) so
  // several scenes can never be open at once and each card manages
  // its own body-scroll lock.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileRef = useRef(null);
  const refFileRef = useRef(null);
  const sceneRefs = Array.isArray(scene.reference_images) ? scene.reference_images : [];

  const loadPrompts = async () => {
    setPromptsLoading(true);
    try {
      const data = await getScenePrompts(storyboardId, scene.scene_id);
      setPromptsData(data);
      // Seed the drafts with what would currently be sent (override OR
      // auto). The user can edit and save to pin a new override.
      setImagePromptDraft(data?.image?.effective_prompt || "");
      setVideoPromptDraft(data?.video?.effective_prompt || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load prompts");
    } finally {
      setPromptsLoading(false);
    }
  };

  const togglePrompts = async () => {
    const next = !promptsOpen;
    setPromptsOpen(next);
    if (next && !promptsData) await loadPrompts();
  };

  const saveImagePromptOverride = async (value) => {
    setSavingPrompts(true);
    try {
      await updateStoryboardScene(storyboardId, scene.scene_id, {
        imagePromptOverride: value,
      });
      toast.success(
        value ? "Image prompt override saved" : "Image prompt reset to auto",
      );
      await loadPrompts();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save prompt");
    } finally {
      setSavingPrompts(false);
    }
  };

  const saveVideoPromptOverride = async (value) => {
    setSavingPrompts(true);
    try {
      await updateStoryboardScene(storyboardId, scene.scene_id, {
        videoPromptOverride: value,
      });
      toast.success(
        value ? "Video prompt override saved" : "Video prompt reset to auto",
      );
      await loadPrompts();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save prompt");
    } finally {
      setSavingPrompts(false);
    }
  };

  const downloadSceneImage = async () => {
    if (!scene.image_url) return;
    try {
      const filename =
        scene.image_filename || `scene-${scene.idx + 1}.png`;
      await downloadImageFromUrl(imgUrl(scene.image_url), filename);
    } catch (e) {
      toast.error(e?.message || "Couldn't download image");
    }
  };

  const uploadSceneRef = async (file) => {
    if (!file) return;
    if (sceneRefs.length >= 6) {
      toast.error("Max 6 reference images per scene");
      return;
    }
    setRefUploading(true);
    try {
      await uploadSceneReferenceImage(storyboardId, scene.scene_id, file);
      toast.success("Reference image attached");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload reference");
    } finally {
      setRefUploading(false);
    }
  };

  const removeSceneRef = async (url) => {
    try {
      await deleteSceneReferenceImage(storyboardId, scene.scene_id, url);
      toast.success("Reference removed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove reference");
    }
  };

  // Re-hydrate from the server doc whenever it changes (after replace
  // image, save, preview start/finish, etc).
  useEffect(() => {
    setFields({
      description: scene.description || "",
      dialog: scene.dialog || "",
      audio: scene.audio || "",
      camera: scene.camera || "",
      lighting: scene.lighting || "",
    });
    setDirty(false);
  }, [scene.description, scene.dialog, scene.audio, scene.camera, scene.lighting]);

  const setField = (key, value) => {
    setFields((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await updateStoryboardScene(storyboardId, scene.scene_id, {
        description: fields.description,
        dialog: fields.dialog,
        audio: fields.audio,
        camera: fields.camera,
        lighting: fields.lighting,
      });
      toast.success(`Scene ${scene.idx + 1} saved`);
      setDirty(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const replaceImage = async (file) => {
    if (!file) return;
    setReplacing(true);
    try {
      await updateStoryboardScene(storyboardId, scene.scene_id, { file });
      toast.success("Image replaced");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't replace image");
    } finally {
      setReplacing(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete scene ${scene.idx + 1}?`)) return;
    try {
      await deleteStoryboardScene(storyboardId, scene.scene_id);
      toast.success("Scene removed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageTweak, setImageTweak] = useState("");
  const [spellchecking, setSpellchecking] = useState(false);
  const onSpellcheck = async () => {
    if (spellchecking || generatingImage) return;
    setSpellchecking(true);
    try {
      const res = await spellcheckSceneImage(storyboardId, scene.scene_id);
      toast.success(
        res?.clean
          ? "No spelling issues found"
          : `Found ${res?.issues?.length || 0} spelling issue(s)`,
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spell check failed");
    } finally {
      setSpellchecking(false);
    }
  };
  const onGenerateImage = async (provider, { useDescriptionAsPrompt = false, tweak = "", fromScratch = false } = {}) => {
    setGeneratingImage(true);
    try {
      const opts = { provider };
      const tweakText = (tweak || "").trim();
      if (fromScratch) {
        // Persist any unsaved Description edits first so the render
        // matches what's on screen. The from-scratch path uses only
        // the description — no channel prefixes, no refs — so this
        // is the single most important text on the page.
        if (dirty) await saveAll();
        const desc = (fields?.description || scene.description || "").trim();
        if (!desc) {
          toast.error("Add a Scene Description first");
          setGeneratingImage(false);
          return;
        }
        opts.prompt = desc;
        opts.fromScratch = true;
      } else if (useDescriptionAsPrompt || tweakText) {
        // Persist any unsaved Description edits first so the image
        // matches the text the user sees on screen.
        if (dirty) await saveAll();
        const desc = (fields?.description || scene.description || "").trim();
        if (!desc) {
          toast.error("Add a Scene Description first");
          setGeneratingImage(false);
          return;
        }
        if (tweakText) {
          // Tweak mode: send JUST the tweak text + a flag telling the
          // backend to use the scene's existing image as the EDIT
          // SOURCE (via gpt-image-1's /v1/images/edits endpoint).
          // We deliberately do NOT prepend the full description here
          // because the backend uses a tweak-specific wrapped prompt
          // that says "keep everything else identical, ONLY apply
          // this change" — the description would otherwise re-anchor
          // the model on the original composition and undo unrelated
          // user-edited details.
          opts.prompt = tweakText;
          opts.tweak = true;
        } else {
          opts.prompt = desc;
        }
      }
      await generateSceneImage(storyboardId, scene.scene_id, opts);
      toast.success(
        opts.fromScratch
          ? "Rendered from scratch (no channel refs)"
          : tweakText
            ? "ChatGPT re-rendered with your tweak"
            : provider === "gemini-nano-banana-pro"
              ? "Nano Banana rendered the scene"
              : "ChatGPT rendered the scene",
      );
      if (tweakText) setImageTweak("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate image");
    } finally {
      setGeneratingImage(false);
    }
  };

  const onPreview = async () => {
    if (!(fields.description || "").trim()) {
      toast.error("Add a Scene Description before generating a preview");
      return;
    }
    // Synchronous reentrant lock — `setPreviewing(true)` doesn't take
    // effect until React flushes, so a quick double-click could fire
    // TWO startStoryboardScenePreview() calls before the button
    // disables. This ref blocks the second click instantly. The
    // backend's atomic claim is the ultimate safety net, but this
    // saves a wasted network round-trip and the awkward
    // "already_running" toast.
    if (previewInFlightRef.current) return;
    previewInFlightRef.current = true;
    if (dirty) {
      // Persist unsaved field edits so Seedance renders the latest text.
      await saveAll();
    }
    setPreviewing(true);
    try {
      await startStoryboardScenePreview(storyboardId, scene.scene_id, {
        correctionNote,
        provider: previewProvider,
      });
      // The note is one-shot — clear it after kicking off the render
      // so the user has to opt in again on the next re-render.
      setCorrectionNote("");
      toast.success(
        correctionNote
          ? "Seedance is re-rendering with your correction…"
          : "Seedance is rendering your preview…",
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start preview");
    } finally {
      setPreviewing(false);
      previewInFlightRef.current = false;
    }
  };

  const status = scene.preview_status || "idle";
  return (
    <article
      data-testid={`storyboard-scene-${scene.idx}`}
      data-scene-anchor={scene.scene_id}
      className="border border-zinc-800 rounded-sm bg-black p-5 space-y-5 scroll-mt-8"
    >
      <div className="flex items-center justify-between gap-3 border-b border-zinc-900 pb-3">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold">
          // SCENE {scene.idx + 1}
        </div>
        <button
          type="button"
          data-testid={`storyboard-scene-delete-${scene.idx}`}
          onClick={onDelete}
          className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
          title="Delete scene"
          aria-label="Delete scene"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Thumbnail row — image + replace button only. The thumbnail
          itself is now clickable: hover reveals an "Upload your own"
          overlay so users can swap the image without hunting for the
          Replace button. The Replace button stays for keyboard /
          screen-reader access. */}
      <div className="flex flex-wrap items-start gap-4">
        <button
          type="button"
          onClick={() => {
            // Defense-in-depth: if the lightbox is already open,
            // ignore synthetic click bubbles from the portalled
            // overlay (React 18 bubbles synthetic events through
            // the virtual tree, so a lightbox close click can
            // synthetically reach us even though we're not the
            // DOM ancestor). The close handlers on the overlay
            // also stopPropagation, so this is a belt-AND-braces.
            if (lightboxOpen) return;
            // Two-mode click: full-size lightbox when the tile has
            // an image, upload picker when it doesn't. The
            // "Upload your own" button next to the tile still
            // provides a keyboard-reachable path for replacement.
            if (scene.image_url && !imageLoadFailed) {
              setLightboxOpen(true);
            } else {
              fileRef.current?.click();
            }
          }}
          disabled={replacing || generatingImage}
          data-testid={`storyboard-scene-image-tile-${scene.idx}`}
          title={
            scene.image_url && !imageLoadFailed
              ? "Click to view full size — use the Upload your own button to replace"
              : "Click to upload your own image for this scene"
          }
          aria-label={
            scene.image_url && !imageLoadFailed
              ? `View scene ${scene.idx + 1} full size`
              : "Click to upload a scene image"
          }
          className="group relative w-56 shrink-0 aspect-video bg-zinc-950 border border-zinc-900 hover:border-yellow-400/60 rounded-sm overflow-hidden transition-colors cursor-pointer disabled:cursor-not-allowed"
        >
          {scene.image_url && !imageLoadFailed ? (
            <img
              data-testid={`storyboard-scene-image-${scene.idx}`}
              src={imgUrl(scene.image_url)}
              alt={`Scene ${scene.idx + 1}`}
              className="w-full h-full object-cover"
              onError={() => {
                // GridFS blob is missing/corrupt — the backend
                // self-heal will clear image_url on next read, but
                // surfacing this immediately gives the user a
                // clickable affordance instead of a silent broken
                // tile. (Support recommendation #1.)
                setImageLoadFailed(true);
              }}
            />
          ) : scene.image_url && imageLoadFailed ? (
            <div
              data-testid={`storyboard-scene-image-broken-${scene.idx}`}
              className="w-full h-full flex flex-col items-center justify-center gap-1 bg-red-950/30 text-red-200 text-[10px] font-mono uppercase tracking-[0.25em] px-3 text-center"
              title="The stored image file is missing or corrupt. Click to upload your own, or use Create Image / Re-generate."
            >
              <X className="w-5 h-5" />
              <span>Image broken</span>
              <span className="text-red-300/80 normal-case tracking-normal text-[10px]">
                click to re-upload, or hit Re-generate
              </span>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-yellow-300/80 hover:text-yellow-200 text-xs font-mono uppercase tracking-[0.25em]">
              <Upload className="w-5 h-5" />
              <span>Click to upload</span>
            </div>
          )}
          {/* Hover overlay — only shows when an image already exists.
              For an empty scene, the placeholder above already says
              "Click to upload". */}
          {scene.image_url && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/70 text-yellow-300 text-xs font-mono uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {replacing
                ? <><Loader2 className="w-5 h-5 animate-spin" /><span>uploading…</span></>
                : <><Maximize2 className="w-5 h-5" /><span>Click to view full size</span></>}
            </div>
          )}
          {/* Lightbox: full-size scene image in a portal-style
              overlay. Rendered inline (not through a Dialog) to
              avoid the shadcn Dialog's max-width constraint — a
              full-bleed scene image is the whole point. */}
          {lightboxOpen && scene.image_url && (
            <SceneImageLightbox
              src={imgUrl(scene.image_url)}
              alt={`Scene ${scene.idx + 1}`}
              sceneIdx={scene.idx}
              onClose={() => setLightboxOpen(false)}
            />
          )}
        </button>
        <div className="flex-1 min-w-[180px] flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) replaceImage(f);
              e.target.value = "";
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid={`storyboard-scene-replace-${scene.idx}`}
              onClick={() => fileRef.current?.click()}
              disabled={replacing || generatingImage}
              className="inline-flex items-center gap-2 font-mono text-[12px] font-bold uppercase tracking-[0.2em] border-2 border-yellow-400 hover:border-yellow-300 text-yellow-100 hover:text-white hover:bg-yellow-400/15 px-3 py-2 rounded-sm transition-colors disabled:opacity-80"
            >
              <Upload className="w-4 h-4" /> {replacing ? "uploading…" : "Upload your own"}
            </button>
            <button
              type="button"
              data-testid={`storyboard-scene-create-image-${scene.idx}`}
              onClick={() => onGenerateImage("openai-gpt-image", { useDescriptionAsPrompt: true })}
              disabled={generatingImage || replacing}
              title="Use ChatGPT (GPT Image 1) to render this scene from the Scene Description"
              className="inline-flex items-center gap-2 font-mono text-[12px] font-bold uppercase tracking-[0.2em] border-2 border-emerald-400 hover:border-emerald-300 text-white bg-emerald-400/25 hover:bg-emerald-400/40 px-3 py-2 rounded-sm transition-colors disabled:opacity-80"
            >
              {generatingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {generatingImage ? "rendering…" : "Create image"}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid={`storyboard-scene-generate-openai-${scene.idx}`}
              onClick={() => onGenerateImage("openai-gpt-image", { useDescriptionAsPrompt: true })}
              disabled={generatingImage || replacing}
              title="Render via GPT Image 1 from Scene Description + your reference images"
              className="inline-flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.2em] border-2 border-emerald-400 hover:border-emerald-300 text-white bg-emerald-400/20 hover:bg-emerald-400/35 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-80"
            >
              {generatingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              GPT Image
            </button>
            <button
              type="button"
              data-testid={`storyboard-scene-generate-gemini-${scene.idx}`}
              onClick={() => onGenerateImage("gemini-nano-banana-pro", { useDescriptionAsPrompt: true })}
              disabled={generatingImage || replacing}
              title="Render via Gemini Nano Banana from Scene Description + your reference images"
              className="inline-flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.2em] border-2 border-cyan-400 hover:border-cyan-300 text-white bg-cyan-400/20 hover:bg-cyan-400/35 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-80"
            >
              {generatingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Nano Banana
            </button>
            <button
              type="button"
              data-testid={`storyboard-scene-generate-scratch-${scene.idx}`}
              onClick={() => onGenerateImage("openai-gpt-image", { fromScratch: true })}
              disabled={generatingImage || replacing}
              title="Ignore ALL channel style refs, cast library, style preset. Renders from just the Scene Description — same energy as prompting ChatGPT directly. Best for breaking out of same-y regenerate loops."
              className="inline-flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.2em] border-2 border-fuchsia-400 hover:border-fuchsia-300 text-white bg-fuchsia-400/20 hover:bg-fuchsia-400/35 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-80"
            >
              {generatingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              From scratch
            </button>
          </div>
          {/* Tweak prompt — appends a free-text correction to the
              scene description and re-renders the image via GPT
              Image 1. Use cases: "make Bella's coat darker", "the
              library has columns out front", "remove the bunny". */}
          <div className="space-y-1.5 pt-1">
            <label
              htmlFor={`scene-image-tweak-${scene.idx}`}
              className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-white block"
            >
              Tweak image{" "}
              <span className="text-zinc-200 font-normal normal-case tracking-normal">
                — appended to the prompt for one re-render
              </span>
            </label>
            <div className="flex gap-2">
              <input
                id={`scene-image-tweak-${scene.idx}`}
                data-testid={`storyboard-scene-image-tweak-${scene.idx}`}
                type="text"
                value={imageTweak}
                onChange={(e) => setImageTweak(e.target.value)}
                disabled={generatingImage}
                maxLength={300}
                placeholder='e.g. "make her shirt blue" or "remove the bunny"'
                onKeyDown={(e) => {
                  if (e.key === "Enter" && imageTweak.trim() && !generatingImage) {
                    e.preventDefault();
                    onGenerateImage("openai-gpt-image", { tweak: imageTweak });
                  }
                }}
                className="flex-1 bg-black border-2 border-zinc-600 focus:border-emerald-400 rounded-sm px-3 py-2 text-base text-white placeholder-zinc-400 focus:outline-none disabled:opacity-70"
              />
              <button
                type="button"
                data-testid={`storyboard-scene-image-tweak-submit-${scene.idx}`}
                onClick={() => onGenerateImage("openai-gpt-image", { tweak: imageTweak })}
                disabled={generatingImage || !imageTweak.trim()}
                title="Apply this tweak and regenerate the scene image via ChatGPT"
                className="inline-flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.2em] border-2 border-emerald-400 hover:border-emerald-300 text-white bg-emerald-400/25 hover:bg-emerald-400/40 px-3 py-2 rounded-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {generatingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                Tweak & regenerate
              </button>
            </div>
          </div>
          {/* Auto spell-check result (vision LLM proofreads on-image
              text after every render). "Fix & re-render" routes the
              corrections through the existing tweak flow so the rest
              of the image stays untouched. */}
          <div className="pt-1 flex flex-wrap items-center gap-2">
            <SpellcheckBadge
              spellcheck={scene.spellcheck}
              busy={generatingImage}
              onFix={(note) => onGenerateImage("openai-gpt-image", { tweak: note })}
              testidPrefix={`storyboard-scene-spellcheck-${scene.idx}`}
            />
            {scene.image_url && (
              <button
                type="button"
                data-testid={`storyboard-scene-spellcheck-run-${scene.idx}`}
                onClick={onSpellcheck}
                disabled={spellchecking || generatingImage}
                title="Proofread all text in this image with a vision LLM"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-yellow-400 disabled:opacity-40 transition-colors"
              >
                {spellchecking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                {spellchecking ? "checking text…" : scene.spellcheck ? "re-check text" : "check text spelling"}
              </button>
            )}
          </div>
          {/* The OLD baked-in `image_prompt` field used to be shown
              here, but it leaked stale 2D wording from the initial
              channel breakdown and confused users into thinking the
              system was rendering with that text. We now expose a
              real "Show prompts" panel below that pulls the LIVE
              composed text from the backend (or the user's pinned
              override) — fully editable. */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              data-testid={`storyboard-scene-toggle-prompts-${scene.idx}`}
              onClick={togglePrompts}
              className="inline-flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-white hover:text-yellow-200 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              {promptsOpen ? "Hide prompts" : "Show & edit prompts"}
            </button>
            {scene.image_url && (
              <button
                type="button"
                data-testid={`storyboard-scene-download-image-${scene.idx}`}
                onClick={downloadSceneImage}
                title="Download this scene image as PNG"
                className="inline-flex items-center gap-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-white hover:text-yellow-200 transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download image
              </button>
            )}
          </div>
          {/* Per-scene reference images — these are stacked FIRST when
              gpt-image-1 renders this scene so the result anchors hardest
              on exactly what the user uploaded for this shot. */}
          <div className="pt-1 border-t border-zinc-900 mt-1">
            <input
              ref={refFileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadSceneRef(f);
                e.target.value = "";
              }}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white"
                title="Refs uploaded on ANY scene of this video apply to every scene automatically. The current scene's own refs win first."
              >
                refs · used on every scene
              </span>
              {sceneRefs.map((u) => (
                <div
                  key={u}
                  className="relative w-12 h-12 border border-zinc-800 rounded-sm overflow-hidden group bg-zinc-950"
                  data-testid={`storyboard-scene-ref-${scene.idx}-${sceneRefs.indexOf(u)}`}
                >
                  <img src={imgUrl(u)} alt="ref" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeSceneRef(u)}
                    title="Remove reference"
                    aria-label="Remove reference"
                    data-testid={`storyboard-scene-ref-remove-${scene.idx}-${sceneRefs.indexOf(u)}`}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3.5 h-3.5 text-red-300" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid={`storyboard-scene-ref-add-${scene.idx}`}
                onClick={() => refFileRef.current?.click()}
                disabled={refUploading || sceneRefs.length >= 6}
                title={
                  sceneRefs.length >= 6
                    ? "Max 6 reference images per scene"
                    : "Upload a reference image — applies to every scene in this video"
                }
                className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] border-2 border-dashed border-yellow-400/70 hover:border-yellow-300 text-white hover:text-yellow-200 hover:bg-yellow-400/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-60"
              >
                {refUploading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ImagePlus className="w-3.5 h-3.5" />
                )}
                {refUploading ? "uploading…" : "Add ref"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Prompts panel — shows the LIVE composed prompt that would be
          sent for both the Image and the Video preview, lets the user
          override with custom text, and reset back to auto. The panel
          fetches from /scenes/{id}/prompts so it never lies about
          what's actually in flight. */}
      {promptsOpen && (
        <div
          data-testid={`storyboard-scene-prompts-panel-${scene.idx}`}
          className="border border-zinc-800 rounded-sm bg-zinc-950 p-4 space-y-5"
        >
          {promptsLoading && !promptsData ? (
            <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-[0.25em]">
              <Loader2 className="w-3 h-3 animate-spin" /> loading prompts…
            </div>
          ) : (
            <>
              <PromptBlock
                title="Image prompt"
                hint="Sent to gpt-image-1 when you click Create Image. Override below to render with custom text; clear it to fall back to auto."
                draft={imagePromptDraft}
                setDraft={setImagePromptDraft}
                isOverride={!!promptsData?.image?.is_override}
                autoPrompt={promptsData?.image?.auto_prompt}
                refImageUrls={promptsData?.image?.ref_image_urls || []}
                onSave={() => saveImagePromptOverride(imagePromptDraft.trim())}
                onReset={() => {
                  setImagePromptDraft(promptsData?.image?.auto_prompt || "");
                  saveImagePromptOverride("");
                }}
                onRegenerate={async () => {
                  // Save the current draft as override first (only if
                  // the user changed anything since last fetch), then
                  // kick off Create Image.
                  const draftTrimmed = imagePromptDraft.trim();
                  const currentEffective =
                    (promptsData?.image?.effective_prompt || "").trim();
                  if (draftTrimmed !== currentEffective) {
                    await saveImagePromptOverride(draftTrimmed);
                  }
                  await onGenerateImage("openai-gpt-image");
                }}
                saving={savingPrompts}
                rows={8}
                testIdPrefix={`storyboard-scene-image-prompt-${scene.idx}`}
              />
              {!stillsOnly && (
              <PromptBlock
                title="Video prompt"
                hint="Sent to Seedance / Kling when you click Generate Preview. Override below to render with custom text; clear it to fall back to auto-built (description + dialog + camera + lighting + audio)."
                draft={videoPromptDraft}
                setDraft={setVideoPromptDraft}
                isOverride={!!promptsData?.video?.is_override}
                autoPrompt={promptsData?.video?.auto_prompt}
                refImageUrls={[]}
                onSave={() => saveVideoPromptOverride(videoPromptDraft.trim())}
                onReset={() => {
                  setVideoPromptDraft(promptsData?.video?.auto_prompt || "");
                  saveVideoPromptOverride("");
                }}
                onRegenerate={async () => {
                  const draftTrimmed = videoPromptDraft.trim();
                  const currentEffective =
                    (promptsData?.video?.effective_prompt || "").trim();
                  if (draftTrimmed !== currentEffective) {
                    await saveVideoPromptOverride(draftTrimmed);
                  }
                  await onPreview();
                }}
                saving={savingPrompts}
                rows={6}
                testIdPrefix={`storyboard-scene-video-prompt-${scene.idx}`}
              />
              )}
            </>
          )}
        </div>
      )}

      {/* Structured template fields — same set as Scene Description Creation */}
      <div className="space-y-4">
        {TEMPLATE_FIELDS.map((f) => (
          <SceneFieldTextarea
            key={f.key}
            testId={`storyboard-scene-${f.key}-${scene.idx}`}
            label={f.label}
            required={f.required}
            value={fields[f.key]}
            onChange={(v) => setField(f.key, v)}
            rows={f.rows}
            placeholder={f.placeholder}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid={`storyboard-scene-save-${scene.idx}`}
          onClick={saveAll}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-4 py-2 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "saving…" : "Save scene"}
        </button>
        {!stillsOnly && (
          <>
            <div data-testid={`storyboard-scene-provider-wrap-${scene.idx}`}>
              <VideoProviderSelect
                value={previewProvider}
                onChange={setPreviewProvider}
                testIdPrefix={`storyboard-scene-provider-${scene.idx}`}
                disabled={previewing || status === "running"}
              />
            </div>
            <button
              type="button"
              data-testid={`storyboard-scene-preview-${scene.idx}`}
              onClick={onPreview}
              disabled={previewing || status === "running"}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] border border-emerald-400/60 text-emerald-200 hover:bg-emerald-400/10 font-bold px-4 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="w-3.5 h-3.5" />
              {status === "running"
                ? "rendering…"
                : status === "ready"
                  ? "Re-render preview"
                  : "Render preview"}
            </button>
          </>
        )}
        {stillsOnly && (
          <span
            data-testid={`storyboard-scene-stills-badge-${scene.idx}`}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] border border-yellow-400/40 bg-yellow-400/5 text-yellow-300 px-3 py-2 rounded-sm"
          >
            stills-only channel · no preview video
          </span>
        )}
      </div>

      {/* One-shot correction note — short instruction appended to the
          prompt for THIS render only. Saves the user from having to
          rewrite the whole prompt or pin a permanent override just to
          tell Seedance "actually the car moves FORWARD, not backward". */}
      {!stillsOnly && (
      <label className="block space-y-1.5">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 flex items-center gap-2">
          One-shot correction
          <span className="font-normal tracking-normal normal-case text-zinc-500">
            — appended to the prompt for the next re-render only
          </span>
        </span>
        <input
          data-testid={`storyboard-scene-correction-${scene.idx}`}
          value={correctionNote}
          onChange={(e) => setCorrectionNote(e.target.value)}
          disabled={previewing || status === "running"}
          maxLength={600}
          placeholder='e.g. "the car moves FORWARD, not backward" or "Bella waves with her left hand"'
          className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-100 placeholder-zinc-600 focus:border-emerald-400/60 focus:outline-none disabled:opacity-50"
        />
      </label>
      )}

      {/* Preview window — full-width below the scene content */}
      <PreviewPanel scene={scene} storyboardId={storyboardId} onChanged={onChanged} stillsOnly={stillsOnly} />
    </article>
  );
}

function SceneFieldTextarea({ label, required, value, onChange, placeholder, rows = 3, testId, actionSlot = null }) {
  return (
    <label className="block space-y-1.5">
      <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200 flex items-center justify-between gap-2 flex-wrap">
        <span className="flex items-center gap-1">
          {label}
          {required && <span className="text-yellow-400">*</span>}
        </span>
        {actionSlot}
      </span>
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100 leading-relaxed resize-y"
      />
    </label>
  );
}

function PreviewPanel({ scene, storyboardId, onChanged, stillsOnly = false }) {
  // For stills-only channels the whole video-preview surface is
  // meaningless — no per-scene MP4 renders, no takes, no approvals.
  // Swap in a compact "this scene ships as a still" pip so the card
  // still reads as complete once the image is generated.
  if (stillsOnly) {
    return (
      <div
        data-testid={`storyboard-preview-panel-stills-${scene.idx}`}
        className="border border-yellow-400/30 bg-yellow-400/5 rounded-sm p-4 flex items-center gap-3"
      >
        <div className="w-2 h-2 rounded-full bg-yellow-400" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-300 font-bold">
            Stills mode
          </div>
          <div className="text-xs text-zinc-300 leading-relaxed">
            This scene ships as a still frame in the slideshow. No preview video
            will be rendered. The final MP4 pans/zooms across this image while
            the narration plays.
          </div>
        </div>
      </div>
    );
  }
  // Each Re-Render opens a brand new VersionCard — old cards stay
  // visible until the user explicitly deletes them. Newest take
  // appears at the top. Active renders show as their own "rendering"
  // card so the user can still play / approve / delete previous
  // takes while a new one is in flight.
  const status = scene.preview_status || "idle";
  const versions = scene.video_versions || [];
  const approvedId = (scene.approved_version_id || "").trim();
  // Chronological order: oldest take on the LEFT, newest on the
  // RIGHT, with the active "rendering" card pinned all the way to
  // the right because it's the most recent take in flight.
  const chronological = [...versions];
  const isRendering = status === "running";
  // Surface a failure card ANY time the latest render failed — even
  // if the scene has prior successful takes. Previously we only
  // showed the failure card when `versions.length === 0`, which
  // silently hid errors from providers like Kling / Veo whenever a
  // Seedance take already existed. Symptom: the "rendering" card
  // disappeared and left ZERO feedback that the render actually
  // failed. See ticket "VEO3/Kling scene preview disappears".
  const showFailure = status === "failed";

  return (
    <div
      data-testid={`storyboard-preview-panel-${scene.idx}`}
      className="flex flex-wrap gap-3 items-start"
    >
      {showFailure && (
        <EmptyPreviewCard
          scene={scene}
          variant="failed"
        />
      )}
      {status === "idle" && versions.length === 0 && (
        <EmptyPreviewCard scene={scene} variant="idle" />
      )}
      {chronological.map((v) => (
        <VersionCard
          key={v.version_id}
          scene={scene}
          storyboardId={storyboardId}
          version={v}
          isApproved={v.version_id === approvedId}
          onChanged={onChanged}
        />
      ))}
      {isRendering && (
        <ActiveRenderCard
          scene={scene}
          storyboardId={storyboardId}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function ActiveRenderCard({ scene, storyboardId, onChanged }) {
  // Card shown while Seedance is busy on a fresh render. Old takes
  // stay visible below this card so the user can keep working.
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    const startMs = scene.preview_started_at
      ? new Date(scene.preview_started_at).getTime()
      : Date.now();
    const tick = () => {
      setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scene.preview_started_at]);
  const sourceImg = imgUrl(scene.image_url);

  return (
    <div
      data-testid={`storyboard-preview-running-${scene.idx}`}
      className="w-80 shrink-0 rounded-sm overflow-hidden border border-yellow-400/70 bg-black"
    >
      <div className="aspect-video relative">
        {sourceImg && (
          <img
            src={sourceImg}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60">
          <div className="w-9 h-9 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
            Seedance rendering
          </div>
          <div
            data-testid={`storyboard-preview-elapsed-${scene.idx}`}
            className="font-mono text-base text-yellow-100 tabular-nums font-bold"
          >
            {fmtMMSS(elapsedSec)}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-400">
            usually takes 3 to 5 minutes
          </div>
          {elapsedSec >= 60 && (
            <CancelRenderButton
              storyboardId={storyboardId}
              sceneId={scene.scene_id}
              sceneIdx={scene.idx}
              elapsedSec={elapsedSec}
              onChanged={onChanged}
            />
          )}
        </div>
      </div>
      <div className="px-3 py-1.5 border-t border-zinc-900 flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-[0.25em]">
        <span className="text-yellow-400 font-bold">
          ↻ RENDERING · {fmtMMSS(elapsedSec)}
        </span>
        <span className="text-zinc-500 normal-case tracking-normal">
          Seedance 2.0 · 15s · ~$0.90
        </span>
      </div>
    </div>
  );
}

function EmptyPreviewCard({ scene, variant }) {
  const sourceImg = imgUrl(scene.image_url);
  const isFailed = variant === "failed";
  return (
    <div
      data-testid={`storyboard-preview-${isFailed ? "failed" : "idle"}-${scene.idx}`}
      className={
        "w-80 shrink-0 rounded-sm overflow-hidden border bg-black " +
        (isFailed ? "border-red-500/70" : "border-zinc-800")
      }
    >
      <div className="aspect-video relative">
        {sourceImg && (
          <img
            src={sourceImg}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center bg-black/70">
          {isFailed ? (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold">
                Preview failed
              </div>
              <div className="text-[10px] text-zinc-300 max-w-sm leading-relaxed">
                {scene.preview_error || "no output"}
              </div>
            </>
          ) : (
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-400 leading-relaxed">
              // click <span className="text-emerald-300">Generate preview</span>
              <br />
              to render a 15s seedance clip
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionCard({ scene, storyboardId, version, isApproved, isLegacy, onChanged }) {
  // One self-contained card per rendered take. Has its own <video>
  // player + approve + download + delete buttons so the user can
  // compare side-by-side and pick the keeper.
  const [busy, setBusy] = useState(""); // "approve" | "delete"
  const [videoLoadError, setVideoLoadError] = useState(false);
  const src = imgUrl(version.url);
  const sourceImg = imgUrl(scene.image_url);

  const onApprove = async () => {
    if (busy) return;
    if (isLegacy) {
      toast.info("Re-render this scene to enable per-take approvals");
      return;
    }
    setBusy("approve");
    try {
      const res = await approveSceneVersion(
        storyboardId, scene.scene_id, version.version_id,
      );
      toast.success(
        res?.approved_version_id
          ? `Scene ${(scene.idx ?? 0) + 1} take approved`
          : `Scene ${(scene.idx ?? 0) + 1} approval cleared`,
      );
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update approval");
    } finally {
      setBusy("");
    }
  };

  const onDelete = async () => {
    if (busy) return;
    if (isLegacy) {
      toast.info("Re-render to replace this legacy take");
      return;
    }
    if (isApproved) {
      // Server-side guard (409) will also block this, but bail
      // early here for a crisper UX — no round-trip needed.
      toast.error("Un-approve this take before deleting it");
      return;
    }
    if (!window.confirm(
      `Delete this take of Scene ${(scene.idx ?? 0) + 1}? This can't be undone.`,
    )) return;
    setBusy("delete");
    try {
      await deleteSceneVersion(storyboardId, scene.scene_id, version.version_id);
      toast.success(`Scene ${(scene.idx ?? 0) + 1} take deleted`);
      // Bump the in-flight request generation BEFORE calling load so
      // any older polling response that's still in flight gets
      // dropped — otherwise the deleted card would briefly re-appear
      // when stale poll data arrives.
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete take");
    } finally {
      setBusy("");
    }
  };

  const fmtTs = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return String(iso).slice(0, 16);
    }
  };

  const borderClass = videoLoadError
    ? "border-red-500/70"
    : isApproved
      ? "border-emerald-400/70"
      : "border-zinc-800";

  return (
    <div
      data-testid={`storyboard-version-card-${scene.idx}-${version.version_id}`}
      className={`w-80 shrink-0 rounded-sm overflow-hidden border ${borderClass} bg-black`}
    >
      <div className="aspect-video relative">
        {!videoLoadError && src ? (
          <video
            data-testid={`storyboard-version-video-${scene.idx}-${version.version_id}`}
            src={src}
            controls
            playsInline
            preload="metadata"
            poster={sourceImg || undefined}
            onError={() => setVideoLoadError(true)}
            className="absolute inset-0 w-full h-full bg-black"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center bg-black/80">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-400 font-bold">
              Couldn&apos;t load video
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <a
                href={src}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 border border-cyan-400/60 hover:border-cyan-300 px-2 py-1 rounded-sm"
              >
                ↗ open mp4 in new tab
              </a>
              {!isLegacy && (
                <button
                  type="button"
                  data-testid={`storyboard-version-prune-${scene.idx}-${version.version_id}`}
                  onClick={onDelete}
                  disabled={!!busy || isApproved}
                  title={isApproved
                    ? "Un-approve this take before removing it"
                    : "This take's video file is missing. Click to remove the broken entry from this scene."}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-300 hover:text-red-100 border border-red-500/60 hover:border-red-300 px-2 py-1 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy === "delete" ? "removing…" : "remove broken take"}
                </button>
              )}
            </div>
          </div>
        )}
        {isApproved && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-sm bg-emerald-400 text-black font-mono text-[9px] uppercase tracking-[0.25em] font-bold pointer-events-none">
            ★ approved
          </div>
        )}
      </div>
      <div className="px-3 py-1.5 border-t border-zinc-900 font-mono text-[10px] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isLegacy || version.legacy ? (
            <span className="text-zinc-400">earlier render</span>
          ) : (
            <span className="text-zinc-400 tabular-nums">{fmtTs(version.created_at)}</span>
          )}
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-300">${Number(version.cost_usd || 0).toFixed(2)}</span>
          {(isLegacy || version.legacy) && (
            <span className="text-zinc-600 italic normal-case">· legacy</span>
          )}
        </div>
        <span
          data-testid={`storyboard-version-provider-${scene.idx}-${version.version_id}`}
          className="text-zinc-300 normal-case tracking-normal text-[10px] border border-zinc-700 bg-zinc-900/60 px-2 py-0.5 rounded-sm"
          title={`Rendered by ${version.provider || "seedance-2.0"}`}
        >
          {PROVIDER_LABEL[version.provider] || version.provider || "Seedance 2.0"}
        </span>
      </div>
      {version.correction_note && (
        <div className="px-3 py-1 border-t border-zinc-900 text-[10px] text-zinc-500 italic">
          ✎ {version.correction_note}
        </div>
      )}
      <div className="px-3 py-2 border-t border-zinc-900 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid={`storyboard-version-approve-${scene.idx}-${version.version_id}`}
          onClick={onApprove}
          disabled={!!busy || isLegacy}
          title={isLegacy
            ? "Legacy take — re-render to enable approvals"
            : (isApproved ? "Click to un-approve" : "Approve this take")}
          className={
            "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm font-bold uppercase tracking-[0.2em] text-[9px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed " +
            (isApproved
              ? "bg-emerald-400 text-black hover:bg-emerald-300"
              : "bg-zinc-900 text-emerald-300 hover:bg-emerald-400/20 border border-emerald-400/40")
          }
        >
          {busy === "approve" ? "…" : (isApproved ? "★ Approved" : "☆ Approve")}
        </button>
        {!videoLoadError && src && (
          <DownloadPreviewButton
            previewSrc={src}
            sceneIdx={scene.idx}
            videoName={`scene_${(scene.idx ?? 0) + 1}_${(version.version_id || "v").slice(0, 6)}.mp4`}
          />
        )}
        <div className="ml-auto">
          <button
            type="button"
            data-testid={`storyboard-version-delete-${scene.idx}-${version.version_id}`}
            onClick={onDelete}
            disabled={!!busy || isLegacy || isApproved}
            title={
              isLegacy
                ? "Legacy take — can't delete"
                : (isApproved
                  ? "Un-approve this take before deleting it"
                  : "Delete this take")
            }
            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-300 border border-zinc-800 hover:border-red-400/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy === "delete" ? "deleting…" : "🗑 delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelRenderButton({ storyboardId, sceneId, sceneIdx, elapsedSec, onChanged }) {
  // Lit up after 60s into a render so the user can unstick a scene
  // whose background task died (eg the pod restarted during a
  // deploy). After 12 minutes (past the 10-min Seedance timeout) the
  // button text switches to "Reset stuck render" to make it obvious
  // the render has been abandoned upstream.
  const [busy, setBusy] = useState(false);
  const stuck = elapsedSec >= 720; // 12 min — past the 10-min timeout
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await cancelScenePreview(storyboardId, sceneId);
      toast.success(`Scene ${sceneIdx + 1} render cancelled`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel render");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid={`storyboard-preview-cancel-${sceneIdx}`}
      onClick={onClick}
      disabled={busy}
      className={
        `mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-sm font-mono text-[10px] uppercase tracking-[0.25em] border transition-colors disabled:opacity-50 ` +
        (stuck
          ? "bg-red-500 text-black border-red-500 hover:bg-red-400 font-bold"
          : "text-zinc-300 border-zinc-700 hover:border-red-400/70 hover:text-red-300")
      }
      title={stuck
        ? "Render is past the 10-minute timeout — click to reset"
        : "Cancel this render"}
    >
      {busy ? "cancelling…" : (stuck ? "✕ reset stuck render" : "cancel render")}
    </button>
  );
}

function DownloadPreviewButton({ previewSrc, sceneIdx, videoName }) {
  const [busy, setBusy] = useState(false);
  // Re-fetch the MP4 ourselves and trigger a real download. Going
  // through a Blob avoids the issue where <a download> is ignored by
  // browsers when the file is served from a different origin (the
  // backend pod vs the React app on the same domain — but on prod our
  // ingress proxies /api to the backend so it's same-origin in
  // practice). We also hit the endpoint with ?download=1 so the
  // server sets Content-Disposition: attachment.
  const download = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      // Strip any existing query string and re-add download=1.
      const u = new URL(previewSrc, window.location.origin);
      u.searchParams.set("download", "1");
      const resp = await fetch(u.toString(), { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = videoName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the blob URL after a tick so the download has time to start.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      toast.success("Preview downloaded");
    } catch (err) {
      toast.error(`Download failed: ${err?.message || "unknown"}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      data-testid={`storyboard-preview-download-btn-${sceneIdx}`}
      className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 border border-cyan-400/40 hover:border-cyan-300 px-1.5 py-0.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <Download className="w-3 h-3" />
      {busy ? "…" : "Download"}
    </button>
  );
}

function fmtMMSS(totalSec) {
  const s = Math.max(0, totalSec | 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

function AddSceneForm({ storyboardId, existingScenes = [], onAdded }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [fields, setFields] = useState({
    description: "",
    dialog: "",
    audio: "",
    camera: "",
    lighting: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // The last existing scene is the source for "Copy from last scene"
  // buttons on audio / camera / lighting. The first scene a user adds
  // has nothing to copy from, so those buttons stay hidden.
  const lastScene = existingScenes.length
    ? existingScenes[existingScenes.length - 1]
    : null;

  // Free the blob URL when the file changes / component unmounts so
  // we don't leak memory on each upload.
  useEffect(() => {
    if (!file) { setPreviewUrl(""); return undefined; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const reset = () => {
    setFile(null);
    setFields({ description: "", dialog: "", audio: "", camera: "", lighting: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  const setField = (key, value) =>
    setFields((f) => ({ ...f, [key]: value }));

  const copyFromLast = (key) => {
    if (!lastScene) return;
    const value = (lastScene[key] || "").trim();
    if (!value) {
      toast.info(`Last scene has no ${key} value to copy`);
      return;
    }
    setField(key, value);
    toast.success(`Copied ${key} from scene ${lastScene.idx + 1}`);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error("Pick an image"); return; }
    if (!fields.description.trim()) {
      toast.error("Scene Description is required");
      return;
    }
    setSubmitting(true);
    try {
      await addStoryboardScene(storyboardId, { file, ...fields });
      toast.success("Scene added");
      reset();
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add scene");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="storyboard-add-scene-form"
      className="border border-cyan-400/30 bg-cyan-400/5 rounded-sm p-5 space-y-4"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-bold">
        // ADD A SCENE
      </div>

      {/* Image picker + thumbnail preview */}
      <div className="space-y-2">
        <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-200 flex items-center gap-1">
          Scene Image <span className="text-yellow-400">*</span>
        </span>
        <div className="flex flex-wrap items-start gap-4">
          <div
            data-testid="storyboard-add-thumbnail"
            className="w-56 shrink-0 aspect-video bg-zinc-950 border border-zinc-900 rounded-sm overflow-hidden flex items-center justify-center"
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="New scene preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                no image yet
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              data-testid="storyboard-add-image-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-zinc-300 file:font-mono file:uppercase file:tracking-[0.25em] file:text-[10px] file:bg-yellow-400 file:text-black file:font-bold file:border-0 file:rounded-sm file:px-3 file:py-2 file:mr-3 file:cursor-pointer"
            />
            {file && (
              <button
                type="button"
                data-testid="storyboard-add-clear-image"
                onClick={() => {
                  setFile(null);
                  // Reset the underlying input so re-picking the same
                  // file fires onChange again (browsers dedupe identical
                  // file selections on the same input element).
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className="self-start font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 border border-zinc-800 hover:border-zinc-600 px-2 py-1 rounded-sm"
              >
                ✕ Clear image
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Template fields — same as Scene Description Creation */}
      <div className="space-y-4 pt-2 border-t border-cyan-400/20">
        {TEMPLATE_FIELDS.map((f) => {
          const showCopy = lastScene && ["audio", "camera", "lighting"].includes(f.key);
          return (
            <SceneFieldTextarea
              key={f.key}
              testId={`storyboard-add-${f.key}`}
              label={f.label}
              required={f.required}
              value={fields[f.key]}
              onChange={(v) => setField(f.key, v)}
              rows={f.rows}
              placeholder={f.placeholder}
              actionSlot={
                showCopy ? (
                  <button
                    type="button"
                    data-testid={`storyboard-add-copy-${f.key}`}
                    onClick={() => copyFromLast(f.key)}
                    title={`Reuse the ${f.label} value from scene ${lastScene.idx + 1}`}
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 border border-cyan-400/40 hover:border-cyan-300 px-2 py-0.5 rounded-sm transition-colors"
                  >
                    ⎘ copy from last scene
                  </button>
                ) : null
              }
            />
          );
        })}
      </div>

      <button
        type="submit"
        data-testid="storyboard-add-submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-cyan-400 hover:bg-cyan-300 text-black font-black px-5 py-2.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Plus className="w-4 h-4" /> {submitting ? "Adding…" : "Add scene"}
      </button>
    </form>
  );
}
